import { noticeLayerUnits, expandNoticeGroupIds, lockedNoticeIds, groupNoticeLayers, ungroupNoticeLayers, dropNoticeLayers } from './noticeLayerGroups';
import { noticeGridClip } from '@cornerstone/shared';
import { cloneNoticeLayers, copyNoticeLayers, readNoticeClipboard, orderNoticeLayers, isNoticeTextInput } from './noticeKeyboard';
import { NoticeKeyboardHelp } from './NoticeKeyboardHelp';
import { readNoticeCanvasPreference, saveNoticeCanvasPreference, validNoticeCanvasSize } from './noticeCanvasPreference';
import { FitModeRadio } from './FitModeRadio';
import { LayerAlignmentControls } from './LayerAlignmentControls';
import { alignNoticeLayers, HANDLE_VISIBLE_PX, nativeMediaBox, noticeWidgetFontAtSize, resizeHandleFrame, resizeBoundsFromHandle, resizeNoticeLayer, resizeNoticeSelection, selectionBounds, layerBounds, type LayerAlignment, type SelectionBounds, type ResizeDirection } from './noticeSelection';
import { fitDataWidgetFrame, fitWeatherWidgetSize, WeatherDisplaySettings } from './WeatherDisplaySettings';
import { useBoOperator } from '../nav/useBoOperator';
import { LocalTimeSettings, TimeWidgetCanvas, WidgetColorField } from './TimeWidgetControls';
import { drawTimeWidget, isLocalTimeSource, isBoWidgetKind, isNewsListSource, splitLegacyClockLayers, drawWeatherWidget, drawPolicyNewsWidget, cornerRadii, cornerRadiusCss, textLineHeight, traceRoundedRect, drawNoticeShape, noticeShapePath, noticeShapeSides, NOTICE_SHAPE_LABELS } from '@cornerstone/shared';
import { WeatherWidgetPreview, WeatherPreviewContext, cachedWeatherPreview, type WeatherPreviewCache } from './WeatherWidgetPreview';
import { PolicyNewsWidgetPreview, newsListSample } from './PolicyNewsWidgetPreview';
import { useContentScopeAccess } from './useContentScopeAccess';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useDrag } from '@use-gesture/react';
import type {
  CalendarEvent,
  ChatRecentMessage,
  Folder,
  MediaItem,
  Notice,
  NoticeDataWidgetLayer,
  NoticeBrickBriefLayer,
  NoticeBrickBriefSource,
  NoticeCornerRadius,
  NoticeFontWeight,
  NoticeImageLayer,
  NoticeLayer,
  NoticeLottieLayer,
  NoticeRoomCardLayer,
  NoticeRoomTemplateLayer,
  NoticeRoomStatusLayer,
  NoticeShapeKind,
  NoticeShapeLayer,
  NoticeStatus,
  ParsedNotice,
  NoticeTextLayer,
  NoticeVideoLayer,
} from '@cornerstone/shared';
import {
  DEFAULT_NOTICE_DURATION_SEC,
  ROOM_CARD_THEMES,
  ROOM_CARD_THEME_TOKENS,
  ROOM_TEMPLATE_CARD_LAYOUTS,
} from '@cornerstone/shared';
import {
  dummyRoomTemplateEvents,
  RoomBoardScaledBox,
  RoomCardScaledBox,
  RoomTemplate,
  computeWindow,
} from '@cornerstone/ui-react';
import { api, API_BASE } from '../client/api';
import { contentGroupHeaders } from '../client/groupScope';
import { pickableMedia } from './groupSections';
import { useLibraryStore } from '../store/library';
import { useCalibrationStore } from '../store/calibration';
import { useDeviceGroupsStore } from '../store/deviceGroups';
import { slideSaveMessage } from './slideSaveNotice';
import { cn } from '../utils/cn';
import { useIsMobile } from '../utils/useIsMobile';
import { Button, Field, Segmented } from './ui';
import { backdropCloseProps, ConfirmModal, InfoTooltip, ModalShell, ToolbarMenu } from './menu';
import { describeApiError, toast } from './toast';
import { TargetPickerModal } from './TargetPickerModal';
import { NoticeGridPresetModal } from './NoticeGridPresetModal';
import { layoutGridLines, readLayoutGrid, saveLayoutGrid, snapToLines } from './noticeLayoutGrid';
import { LayoutGridOverlay, LayoutGridSettings } from './LayoutGridControls';
import { StationCNoticeTemplateGallery } from './StationCNoticeTemplateGallery';
import { resolveStationCTemplateAssets } from './stationcFigmaTemplates';
import { TEMPLATE_ASSET_MEDIA, assetForMediaId } from './stationcTemplateAssets';
import { defaultDateLayer, defaultClockLayer, DATA_WIDGET_LABELS, NoticeDataWidgetModal, NewsSourceSettings, dataWidgetPlaceholder } from './NoticeDataWidgetModal';

// =============================================================================
// NoticeEditorModal — free-placement slide editor.
// Canvas uses its saved virtual dimensions, shown scaled-to-fit inside the
// modal. Layers (text/image/video) are absolutely positioned with pixel
// coords in that virtual space; they're draggable and resizable with pointer
// gestures. Save: rasterize the current preview to a PNG blob and upload as
// the notice thumbnail, then PUT the layers JSON.
// =============================================================================

// Default (landscape) virtual-canvas dimensions. A Scene may instead be
// portrait — see NBL_REQ_409. The active dimensions live in EditorState
// (canvasW/canvasH) and are persisted on the Notice (canvasWidth/Height).
const CANVAS_W = 1920;
const CANVAS_H = 1080;
// UX 재구조화: Add 툴바는 Text/Image/Video/WebM만 노출. Room·템플릿 도구는
// 숨김(코드 보존). 다시 켜려면 true.
const SHOW_TEMPLATE_TOOLS = false;
// 대지 디바이스 프리셋 — 선택 시 캔버스 치수를 설정하고 레이어를 reflow한다.
// 별도 영속화 없음(치수는 canvasW/H로 저장).
const DEVICE_PRESETS: Array<{ label: string; w: number; h: number }> = [
  { label: 'FHD 가로 · 1920×1080', w: 1920, h: 1080 },
  { label: 'FHD 세로 · 1080×1920', w: 1080, h: 1920 },
  { label: 'UHD 가로 · 3840×2160', w: 3840, h: 2160 },
  { label: 'UHD 세로 · 2160×3840', w: 2160, h: 3840 },
  { label: 'HD 가로 · 1280×720', w: 1280, h: 720 },
];
const PORTRAIT_W = CANVAS_H; // 1080
const PORTRAIT_H = CANVAS_W; // 1920

export type SceneOrientation = 'landscape' | 'portrait';

export const orientationOf = (w: number, h: number): SceneOrientation =>
  h > w ? 'portrait' : 'landscape';

type EditorState = {
  name: string;
  durationSec: number;
  layers: NoticeLayer[];
  selectedId: string | null;
  selectedIds?: string[];
  /** Active virtual-canvas dimensions (NBL_REQ_409). */
  canvasW: number;
  canvasH: number;
  /** Canvas (대지) background fill. null = none (transparent over black). */
  canvasFill: string | null;
  /** Fill opacity 0–100. */
  canvasFillOpacity: number;
  canvasFitMode: NonNullable<Notice['fitMode']>;
  /** 대지 생성 여부. 2026-09-16 대교: 새 슬라이드도 대지를 자동으로 갖는다(옛 '대지 생성하기' 단계 제거). 항상 true. */
  hasCanvas: boolean;
};

/**
 * Reflow every layer when the canvas is resized (e.g. landscape↔portrait).
 * Scales each layer's position + size by the per-axis ratio so the relative
 * composition is preserved as closely as a non-uniform resize allows — this
 * is the layer-reposition conversion helper required by NBL_REQ_409 / HNBL-86.
 */
export function reflowLayers(
  layers: NoticeLayer[],
  fromW: number,
  fromH: number,
  toW: number,
  toH: number,
): NoticeLayer[] {
  if (fromW <= 0 || fromH <= 0) return layers;
  const sx = toW / fromW;
  const sy = toH / fromH;
  return layers.map((layer) => resizeNoticeLayer(layer, {
    x: Math.round(layer.x * sx),
    y: Math.round(layer.y * sy),
    width: Math.round(layer.width * sx),
    height: Math.round(layer.height * sy),
  }));
}

function textLayer(
  id: string,
  text: string,
  y: number,
  height: number,
  fontSize: number,
  fontWeight: 400 | 500 | 600 | 700,
): NoticeTextLayer {
  return {
    id: `${id}-${Date.now().toString(36)}`,
    kind: 'text',
    x: 120,
    y,
    width: 1680,
    height,
    z: 10,
    opacity: 1,
    rotation: 0,
    text,
    fontSize,
    fontWeight,
    color: '#ffffff',
    backgroundColor: null,
    align: 'left',
    paddingX: 0,
    paddingY: 0,
  };
}

// BRICK BRIEF — slot-based template that ships as one composite layer. See
// packages/player/src/media/brickBriefCompositor.ts for the Figma-faithful
// renderer used at playback time. The admin canvas shows a light preview
// (background + dark card outline + slot text) since it doesn't load the
// player's SVG assets / fonts at edit time.
function brickBriefTemplate(): NoticeLayer[] {
  return [
    {
      id: `brick-brief-${Date.now().toString(36)}`,
      kind: 'brickBrief',
      x: 0,
      y: 0,
      width: CANVAS_W,
      height: CANVAS_H,
      z: 0,
      opacity: 1,
      rotation: 0,
      slots: {
        header: '회의실 공사 안내',
        title: '구조 변경 및 공간 이용 안내',
        date: '2026년 5월 16일 (토)',
        time: '15:30~16:10',
        location: '본관 7층 회의실',
        content:
          '관련 부서원 분들은 시간 맞춰 회의실로 모여주시기 바랍니다.\n자세한 사항은 담당자에게 문의 부탁드립니다.',
      },
      variant: 'data',
    },
  ];
}

function roomTemplateLayer(): NoticeRoomTemplateLayer {
  return {
    id: `room-template-${Date.now().toString(36)}`,
    kind: 'roomTemplate',
    x: 0,
    y: 0,
    width: CANVAS_W,
    height: CANVAS_H,
    z: 0,
    opacity: 1,
    rotation: 0,
    theme: 'hanoi',
    dataMode: 'dummy',
    calendarIntegrationId: null,
  };
}

export function NoticeEditorModal({
  notice,
  mediaList,
  onClose,
  openTemplateOnMount = false,
  saveTarget,
  ownerGroupId,
}: {
  notice: Notice | null;
  mediaList: MediaItem[];
  onClose: () => void;
  /** ＋ 메뉴 '템플릿에서 만들기' — 새 슬라이드 편집기를 템플릿 갤러리가 열린 상태로 시작(2026-09-16 대교). */
  openTemplateOnMount?: boolean;
  /**
   * 라이브러리 대신 다른 곳(기기 대기 화면)에 저장한다. 이름·폴더를 묻는 저장 팝업과 썸네일을 건너뛰고
   * 편집 결과만 넘긴다 — 슬라이드 목록에 남지 않는다(2026-09-18).
   */
  saveTarget?: { label: string; save: (draft: Pick<Notice, 'name' | 'canvasWidth' | 'canvasHeight' | 'canvasFill' | 'canvasFillOpacity' | 'fitMode' | 'layers'>) => Promise<void> };
  /**
   * 새 슬라이드를 어느 그룹 것으로 만들지(null = 공통). 생략하면 서버 기본(작업 그룹 → 만든 사람).
   * 라이브러리의 '올릴 그룹'에서 고른 값이다(SCDS-93). 기존 슬라이드 수정에는 쓰지 않는다.
   */
  ownerGroupId?: string | null | undefined;
}) {
  const isBo = useBoOperator() === 'allowed';
  // 모바일 — 레이어·캔버스·속성 세 칸이 한 줄에 들어가지 않는다. 위쪽 전환으로 한 칸씩 연다(2026-09-23).
  const isMobile = useIsMobile();
  const [mobilePane, setMobilePane] = useState<'canvas' | 'layers' | 'props'>('canvas');
  const registeredTemplates = useLibraryStore(s => s.notices);
  // Figma 템플릿의 번들 그래픽은 저장할 때 이 조직 라이브러리에 올린다(한 번만, originalName 으로 재사용).
  // 적용만 하고 닫으면 라이브러리에 아무것도 남지 않는다(2026-10-14 대교).
  const uploadTemplateMedia = useCalibrationStore((s) => s.uploadMedia);
  const weatherPreviews = useRef<WeatherPreviewCache>(new Map());
  const createNotice = useLibraryStore((s) => s.createNotice);
  const updateNotice = useLibraryStore((s) => s.updateNotice);
  const uploadThumbnail = useLibraryStore((s) => s.uploadNoticeThumbnail);
  const loadNotices = useLibraryStore((s) => s.loadNotices);
  const deleteNotice = useLibraryStore((s) => s.deleteNotice);
  // 공개 범위(공개/비공개)는 조직 Owner만 바꾼다 — 저장 팝업의 라디오 활성 여부(2026-09-15 대교).
  const scopeAccess = useContentScopeAccess();
  const calendarRooms = useLibraryStore((s) => s.calendarRooms);

  const [preferredCanvas] = useState(() => readNoticeCanvasPreference({ width: CANVAS_W, height: CANVAS_H }));
  const makeInitialState = (): EditorState => ({
    // 새 슬라이드의 이름은 비워 둔다. 예전에는 '새 공지'를 값으로 넣어서, 저장 모달이 그 글자를
    // 그대로 담고 있었다 — 안내문처럼 보이지만 지우지 않으면 진짜 이름이 된다(2026-09-17 대교).
    name: notice?.name ?? '',
    durationSec: notice?.durationSec ?? DEFAULT_NOTICE_DURATION_SEC,
    // 새 Scene은 레이어 없이 시작(대지 생성 후 사용자가 추가). 기존 Scene은 그대로.
    layers: notice ? splitLegacyClockLayers(notice.layers).sort((a, b) => a.z - b.z) : [],
    selectedId: null, selectedIds: [],
    // Honour the Scene's persisted canvas dimensions (NBL_REQ_409); fall back
    // to the landscape default for new scenes or pre-canvasWidth records.
    canvasW: notice?.canvasWidth && notice.canvasWidth > 0 ? notice.canvasWidth : preferredCanvas.width,
    canvasH: notice?.canvasHeight && notice.canvasHeight > 0 ? notice.canvasHeight : preferredCanvas.height,
    canvasFill: notice?.canvasFill ?? null,
    canvasFillOpacity: notice?.canvasFillOpacity ?? 100,
    canvasFitMode: notice?.fitMode ?? 'contain',
    // 새 슬라이드도 기본 치수(마지막에 쓴 대지 크기)로 대지를 바로 만든다(2026-09-16 대교).
    hasCanvas: true,
  });
  // Snapshot of the state the editor opened with — used by the 초기화 (reset)
  // button to restore the editing session to its starting point.
  const initialStateRef = useRef<EditorState>(makeInitialState());
  const [state, setState] = useState<EditorState>(() => initialStateRef.current);

  /** Set explicit canvas dimensions and reflow layers (대지 W/H · 디바이스 프리셋). */
  const applyCanvasSize = (toW: number, toH: number) => {
    if (notice?.canWrite === false || !validNoticeCanvasSize({ width: toW, height: toH })) return;
    saveNoticeCanvasPreference({ width: toW, height: toH });
    setState((s) => {
      if (toW <= 0 || toH <= 0 || (s.canvasW === toW && s.canvasH === toH)) return s;
      return {
        ...s,
        canvasW: toW,
        canvasH: toH,
        layers: reflowLayers(s.layers, s.canvasW, s.canvasH, toW, toH),
      };
    });
  };
  // ---- Undo/Redo (이전단계 / 앞으로) ---------------------------------------
  // History of meaningful edit states (layers, canvas dims/fill, name,
  // duration). selectedId/playhead changes are ignored. Drag produces granular
  // entries — acceptable for v1.
  const pastRef = useRef<EditorState[]>([]);
  const futureRef = useRef<EditorState[]>([]);
  const applyingHistoryRef = useRef(false);
  const nudgeHistoryRef = useRef<{ recorded: boolean } | null>(null);
  /** 포인터 제스처(드래그·크기 조절) 한 번을 되돌리기 한 번으로 묶는다.
   *  예전에는 마우스가 움직일 때마다 기록이 쌓여 되돌리기를 누르면 1px씩 물러났다.
   *  끝(`ended`)은 곧바로 닫지 않는다 — 놓는 순간의 마지막 반영이 아직 남아 있어서,
   *  여기서 바로 비우면 그 1px이 별도 기록으로 새어 나간다. 그 반영까지 합친 뒤 닫는다. */
  const gestureHistoryRef = useRef<{ recorded: boolean; ended: boolean } | null>(null);
  const markGesture = useCallback((phase: 'start' | 'end') => {
    if (phase === 'start') gestureHistoryRef.current = { recorded: false, ended: false };
    else if (gestureHistoryRef.current) gestureHistoryRef.current.ended = true;
  }, []);
  const prevStateRef = useRef<EditorState>(state);
  const [, bumpHist] = useState(0); // force re-render so undo/redo enabled state updates

  useEffect(() => {
    const a = prevStateRef.current;
    const b = state;
    if (applyingHistoryRef.current) {
      applyingHistoryRef.current = false;
      prevStateRef.current = b;
      return;
    }
    const changed =
      a.layers !== b.layers ||
      a.canvasW !== b.canvasW ||
      a.canvasH !== b.canvasH ||
      a.name !== b.name ||
      a.durationSec !== b.durationSec ||
      a.canvasFill !== b.canvasFill ||
      a.canvasFillOpacity !== b.canvasFillOpacity ||
      a.canvasFitMode !== b.canvasFitMode ||
      a.hasCanvas !== b.hasCanvas;
    if (changed) {
      // 키보드 미세 이동(nudge)과 포인터 제스처는 '여러 번의 변화 = 기록 하나'로 합친다.
      const coalesce = nudgeHistoryRef.current ?? gestureHistoryRef.current;
      if (!coalesce?.recorded) {
        pastRef.current.push(a);
        if (pastRef.current.length > 100) pastRef.current.shift();
      }
      if (coalesce) coalesce.recorded = true;
      if (gestureHistoryRef.current?.ended) gestureHistoryRef.current = null;
      futureRef.current = [];
      bumpHist((t) => t + 1);
    }
    prevStateRef.current = b;
  }, [state]);

  const undo = () => {
    const prev = pastRef.current.pop();
    if (!prev) return;
    futureRef.current.push(prevStateRef.current);
    applyingHistoryRef.current = true;
    setState(prev);
    bumpHist((t) => t + 1);
  };
  const redo = () => {
    const next = futureRef.current.pop();
    if (!next) return;
    pastRef.current.push(prevStateRef.current);
    applyingHistoryRef.current = true;
    setState(next);
    bumpHist((t) => t + 1);
  };
  // 되돌리기 확인은 앱 ConfirmModal로 — 네이티브 confirm은 위치·크기·문구가 앱
  // 통제 밖이라 "조작부는 움직이지 않는다" 원칙에 어긋난다(2026-09-16 검수 S19).
  const resetToInitial = () => {
    setState({ ...initialStateRef.current });
  };
  const canUndo = pastRef.current.length > 0;
  const canRedo = futureRef.current.length > 0;

  const [mediaPicker, setMediaPicker] = useState<null | 'image' | 'video' | 'webm'>(null);
  const [gridPresetOpen, setGridPresetOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [keyboardHelpOpen, setKeyboardHelpOpen] = useState(false);
  const [stationCTemplateOpen, setStationCTemplateOpen] = useState(openTemplateOnMount);
  const [aiDraftOpen, setAiDraftOpen] = useState(false);
  const [dataWidgetEditor, setDataWidgetEditor] = useState<string | null>(null);
  const [dataWidgetInitialKind, setDataWidgetInitialKind] = useState<NoticeDataWidgetLayer['source']['kind']>('weather');
  const [aspectLocks, setAspectLocks] = useState<Record<string, boolean>>({});
  const [layoutGrid, setLayoutGrid] = useState(readLayoutGrid);
  useEffect(() => saveLayoutGrid(layoutGrid), [layoutGrid]);
  const [groupAspectLocked, setGroupAspectLocked] = useState(true);
  const readOnly = notice?.canWrite === false;
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  // 대지 크기를 등록된 기기 해상도에서 가져온다(SCDS-115) — 매뉴얼 1.5 '디바이스 선택으로 등록된 기기 해상도를 가져오거나'.
  // 볼 수 있는 기기만 서버가 내려 준다. 플레이리스트 추가 창과 같은 목록이다.
  const allDevices = useDeviceGroupsStore((s) => s.devices);
  const refreshDevices = useDeviceGroupsStore((s) => s.refresh);
  useEffect(() => { void refreshDevices(); }, [refreshDevices]);
  const sizedDevices = useMemo(() => allDevices.flatMap((d) => d.resolutionW && d.resolutionH ? [{ id: d.id, name: d.name, w: d.resolutionW, h: d.resolutionH }] : []), [allDevices]);
  /** 디바이스 선택에서 고른 값. null = 대지 크기로 추정, '' = 직접 입력. 레이어를 골랐다 돌아와도 유지되게 여기 둔다. */
  const [canvasPick, setCanvasPick] = useState<string | null>(null);
  // Inline layer-rename state (layers list). renamingId === layer.id while editing.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  // 레이어 목록 드래그 순서 변경(드래그 중 실시간 재배치).
  const [dragLayerIds, setDragLayerIds] = useState<string[]>([]);
  const [layerDrop, setLayerDrop] = useState<{key:string;ids:string[];after:boolean;intoGroup:string|null}|null>(null);
  const [collapsedGroups,setCollapsedGroups] = useState<Set<string>>(new Set());
  const layerListRef=useRef<HTMLDivElement>(null);
  const layerPointer=useRef<{ids:string[];key:string;x:number;y:number;px:number;py:number;pointerId:number;active:boolean;tap:()=>void;doubleTap:()=>void}|null>(null);
  const layerDropRef=useRef<typeof layerDrop>(null);
  const layerScrollFrame=useRef(0);
  const lastLayerTap=useRef({key:'',time:0});
  useEffect(()=>()=>cancelAnimationFrame(layerScrollFrame.current),[]);
  // 대지 위 텍스트 인라인 편집 — 더블클릭 시 editingId === 텍스트 레이어 id.
  const [editingId, setEditingId] = useState<string | null>(null);

  // 하단 툴바 — 도구(선택/이동) 모드 + 사용자 줌.
  const [tool, setTool] = useState<'select' | 'move'>('select');
  const [zoom, setZoom] = useState(1);
  // 저장 팝업(슬라이드명·재생시간·폴더).
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  // 폴더 상태·조회는 2026-09-16 대교 결정으로 없앴다 — 콘텐츠는 소유 그룹으로만 나뉜다.

  const canvasStageRef = useRef<HTMLDivElement | null>(null);
  const stageBoxRef = useRef<HTMLDivElement | null>(null);
  // fitScale = 캔버스를 스테이지 영역에 contain 시키는 배율(=줌 100% 기준).
  const [fitScale, setFitScale] = useState(1);
  const displayScale = fitScale * zoom;
  const snapLines = useMemo(() => layoutGrid.visible ? layoutGridLines(layoutGrid, state.canvasW, state.canvasH) : null, [layoutGrid, state.canvasW, state.canvasH]);

  const mediaById = useMemo(() => {
    const m = new Map<string, MediaItem>();
    // 저장 전 템플릿 그래픽("asset:" id) — 번들 파일로 그린다. 고르는 창(pickableMediaList)에는 나오지 않는다.
    TEMPLATE_ASSET_MEDIA.forEach((mi) => m.set(mi.id, mi));
    mediaList.forEach((mi) => m.set(mi.id, mi));
    return m;
  }, [mediaList]);
  // 이 슬라이드가 속한(또는 만들어질) 그룹. 고르는 창은 같은 파일의 여러 그룹 사본 중 이 그룹 것을 남긴다.
  const contentGroupId = notice ? notice.ownerGroupId ?? null : ownerGroupId !== undefined ? ownerGroupId : contentGroupHeaders()['x-content-group'] ?? null;
  // 고르는 창에 내놓을 미디어 — 라이브러리와 같은 보기 범위 + 같은 파일 사본 하나로. 권한 확인 전에는 비워 둔다.
  // (mediaById는 전체 목록 그대로 — 이미 놓인 레이어가 다른 사본을 가리켜도 그려져야 한다.)
  const scopeOptions = scopeAccess.options;
  const pickableMediaList = useMemo(() => scopeOptions
    ? pickableMedia(mediaList, scopeOptions.groups ?? [], scopeOptions.actor?.central ?? false, contentGroupId)
    : [], [mediaList, scopeOptions, contentGroupId]);

  // fitScale = contain the virtual canvas inside the stage area (both axes),
  // = zoom 100% baseline. Actual on-screen scale = fitScale * zoom.
  useEffect(() => {
    const area = canvasStageRef.current;
    if (!area) return;
    const update = () => {
      const r = area.getBoundingClientRect();
      const pad = 48; // p-6 padding on the area
      const availW = Math.max(1, r.width - pad);
      const availH = Math.max(1, r.height - pad);
      const fit = Math.min(availW / state.canvasW, availH / state.canvasH);
      if (fit > 0) setFitScale(fit);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(area);
    return () => ro.disconnect();
  }, [state.canvasW, state.canvasH]);

  const idsFor = (s: EditorState): string[] => s.selectedId === null ? [] : s.selectedIds?.includes(s.selectedId) ? s.selectedIds : [s.selectedId];
  const selectedIds = idsFor(state);
  const lockedIds = lockedNoticeIds(state.layers);
  const selectedLayers = state.layers.filter(layer => selectedIds.includes(layer.id));
  const selectionBox = selectionBounds(selectedLayers);
  const [alignmentTarget, setAlignmentTarget] = useState<'selection' | 'canvas'>('selection');
  const [marquee, setMarquee] = useState<SelectionBounds | null>(null);
  const marqueeStart = useRef<{ x: number; y: number; keep: string[] } | null>(null);
  const marqueeMoved = useRef(false);
  const selectLayer = (id: string, additive = false, preserveGroup = false, individual = false) => setState(s => {
    if (additive && lockedNoticeIds(s.layers).has(id)) return s;
    const ids = idsFor(s), target = individual ? [id] : expandNoticeGroupIds(s.layers,[id]);
    const next = additive ? target.every(value=>ids.includes(value)) ? ids.filter(value=>!target.includes(value)) : [...new Set([...ids,...target])]
      : preserveGroup && ids.includes(id) ? ids : target;
    return { ...s, selectedId: next.at(-1) ?? null, selectedIds: next };
  });
  const selectGroup = (ids:string[],additive=false) => setState(s=>{
    const old=idsFor(s),next=additive?ids.every(id=>old.includes(id))?old.filter(id=>!ids.includes(id)):[...new Set([...old,...ids])]:ids;
    return {...s,selectedId:next.at(-1)??null,selectedIds:next};
  });
  const groupSelection = (ungroup=false) => {
    if(readOnly||saving||!ungroup&&wholeGroup)return;
    setState(current=>{
      const ids=idsFor(current), layers=ungroup?ungroupNoticeLayers(current.layers,ids):groupNoticeLayers(current.layers,ids,`그룹 ${new Set(current.layers.flatMap(l=>l.editorGroup?[l.editorGroup.id]:[])).size+1}`);
      return layers===current.layers?current:{...current,layers};
    });
  };
  const selectedGroupId = selectedLayers.length && selectedLayers.every(layer=>layer.editorGroup?.id===selectedLayers[0]!.editorGroup?.id) ? selectedLayers[0]!.editorGroup?.id : undefined;
  const wholeGroup = !!selectedGroupId && state.layers.filter(layer=>layer.editorGroup?.id===selectedGroupId).every(layer=>selectedIds.includes(layer.id));
  const groupActionBlocked = readOnly || saving || selectedIds.some(id=>lockedIds.has(id));
  const alignSelected = (alignment: LayerAlignment) => {
    if (readOnly || saving || selectedIds.some(id=>lockedIds.has(id))) return;
    setState(s => ({ ...s, layers: alignNoticeLayers(s.layers, idsFor(s), alignment,
      idsFor(s).length < 2 || alignmentTarget === 'canvas' ? { x: 0, y: 0, width: s.canvasW, height: s.canvasH } : undefined) }));
  };
  const resizeGroup = (patch: Partial<SelectionBounds>) => {
    if (readOnly || saving) return;
    setState(s => {
      const ids = idsFor(s); if(ids.some(id=>lockedNoticeIds(s.layers).has(id)))return s;
      const box = selectionBounds(s.layers.filter(layer => ids.includes(layer.id) && !layer.locked));
      return box ? { ...s, layers: resizeNoticeSelection(s.layers, ids, { ...box, ...patch }) } : s;
    });
  };
  const dragPatchLayer = (id: string, patch: Partial<NoticeLayer>) => {
    if (readOnly || saving) return;
    setState(s => {
      const ids = idsFor(s), source = s.layers.find(layer => layer.id === id);
      if (!source || lockedNoticeIds(s.layers).has(id)) return s;
      const movingGroup = ids.length > 1 && ids.includes(id) && patch.width === undefined && patch.height === undefined && (patch.x !== undefined || patch.y !== undefined);
      if(movingGroup&&ids.some(value=>lockedNoticeIds(s.layers).has(value)))return s;
      return { ...s, layers: s.layers.map(layer => {
        if (layer.locked) return layer;
        if (movingGroup && ids.includes(layer.id)) return { ...layer, x: layer.x + ((patch.x ?? source.x) - source.x), y: layer.y + ((patch.y ?? source.y) - source.y) };
        return layer.id === id ? resizeNoticeLayer(layer, patch) : layer;
      }) };
    });
  };
  const selectionControls = <LayerAlignmentControls count={selectedLayers.length} disabled={groupActionBlocked || !selectedLayers.length}
    target={alignmentTarget} onTarget={setAlignmentTarget} onAlign={alignSelected} />;
  const selected = state.layers.find((l) => l.id === state.selectedId) ?? null;
  const editedWidget = state.layers.find((layer): layer is NoticeDataWidgetLayer => layer.id === dataWidgetEditor && layer.kind === 'dataWidget');
  const hasInvalidRoomCard = state.layers.some(
    (l) => l.kind === 'roomCard' && !l.calendarIntegrationId.trim(),
  );
  const hasInvalidRoomTemplate = state.layers.some(
    (l) => l.kind === 'roomTemplate' && l.dataMode === 'live' && !l.calendarIntegrationId,
  );

  // ---------------- layer operations ----------------
  const patchLayer = (id: string, patch: Partial<NoticeLayer>) => {
    if (readOnly) return;
    setState((s) => ({
      ...s,
      layers: s.layers.map((l) => {
        if (l.id !== id) return l;
        // BRICK-101 — 잠긴 레이어는 어느 경로로도 수정되지 않는다. 예전엔
        // 캔버스 드래그만 막혀 있어 속성 패널·레이어 목록으로는 그대로
        // 바뀌었고, 그러면 잠금이 사실상 의미가 없었다. 잠금 토글 자체와
        // 표시 여부는 잠긴 상태에서도 가능해야 풀 수 있다.
        const onlyLockOrVisibility =
          Object.keys(patch).every((k) => k === 'locked' || k === 'hidden');
        if (lockedNoticeIds(s.layers).has(l.id) && !onlyLockOrVisibility) return l;
        return resizeNoticeLayer(l, patch);
      }),
    }));
  };
  const removeLayer = (id: string) => {
    if (readOnly) return;
    setState((s) => {
      // 잠긴 레이어는 삭제도 막는다(BRICK-101). 지우려면 먼저 잠금을 푼다.
      if (lockedNoticeIds(s.layers).has(id)) return s;
      return {
        ...s,
        layers: s.layers.filter((l) => l.id !== id),
        selectedId: s.selectedId === id ? null : s.selectedId,
        selectedIds: idsFor(s).filter(selected => selected !== id),
      };
    });
  };
  const addLayer = (layer: NoticeLayer) => {
    if (readOnly) return;
    setState((s) => ({
      ...s,
      layers: [...s.layers, layer],
      selectedId: layer.id, selectedIds: [layer.id],
    }));
  };

  // ---------------- layer label + rename ----------------
  // Custom name if set, else a kind-derived summary for the layers list row.
  const layerLabel = (l: NoticeLayer): string => {
    switch (l.kind) {
      case 'dataWidget':
        return l.title === DATA_WIDGET_LABELS[l.source.kind] ? l.title : `${DATA_WIDGET_LABELS[l.source.kind]} · ${l.title || '정보 위젯'}`;
      case 'image':
      case 'video':
        return mediaById.get(l.mediaId)?.originalName ?? `#${l.id.slice(-6)}`;
      case 'text':
        return l.text.slice(0, 20) || '(empty text)';
      case 'shape':
        return `도형 · ${NOTICE_SHAPE_LABELS[l.shape]}`;
      case 'lottie':
        return `Lottie · ${mediaById.get(l.mediaId)?.originalName ?? 'missing'}`;
      case 'brickBrief':
        return l.slots.header.slice(0, 20) || 'BRICK BRIEF';
      case 'roomStatus':
        return `회의실 보드 (${l.windowStartHour}–${l.windowEndHour}시)`;
      case 'roomCard':
        return `${l.theme} card · ${calendarRooms.find((r) => r.id === l.calendarIntegrationId)?.label ?? '회의실 미선택'}`;
      case 'roomTemplate':
        return `회의실 템플릿 · ${l.theme} · ${l.dataMode === 'dummy' ? '더미' : '실제'}`;
      default:
        return '레이어';
    }
  };
  const startRename = (l: NoticeLayer) => {
    if(readOnly||saving||lockedIds.has(l.id))return;
    setRenameDraft(l.name ?? '');
    setRenamingId(l.id);
  };
  const commitRename = (id: string) => {
    const v = renameDraft.trim();
    patchLayer(id, { name: v || undefined } as Partial<NoticeLayer>);
    setRenamingId(null);
  };
  const addRoomStatusLayer = () => {
    const id = `room-${Date.now().toString(36)}`;
    const maxZ = state.layers.length
      ? Math.max(...state.layers.map((l) => l.z))
      : 0;
    addLayer({
      id,
      kind: 'roomStatus',
      x: 0,
      y: 0,
      width: CANVAS_W / 2,
      height: CANVAS_H,
      z: maxZ + 1,
      opacity: 1,
      rotation: 0,
      roomIntegrationIds: null,
      windowStartHour: 8,
      windowEndHour: 20,
      orientation: 'vertical',
    });
  };
  const addRoomCardLayer = () => {
    const id = `room-card-${Date.now().toString(36)}`;
    const maxZ = state.layers.length
      ? Math.max(...state.layers.map((l) => l.z))
      : 0;
    addLayer({
      id,
      kind: 'roomCard',
      x: 80,
      y: 60,
      width: 1060,
      height: 960,
      z: maxZ + 1,
      opacity: 1,
      rotation: 0,
      calendarIntegrationId: '',
      theme: 'tokyo',
      title: null,
      showLogo: true,
    });
  };
  const cancelLayerPointer = () => {
    const pointer=layerPointer.current,list=layerListRef.current;
    if(pointer&&list?.hasPointerCapture?.(pointer.pointerId))list.releasePointerCapture(pointer.pointerId);
    layerPointer.current=null;layerDropRef.current=null;setLayerDrop(null);setDragLayerIds([]);cancelAnimationFrame(layerScrollFrame.current);
  };
  const updateLayerDrop = (x:number,y:number) => {
    const pointer=layerPointer.current,list=layerListRef.current;
    if(!pointer?.active||!list)return;
    const node=document.elementFromPoint?.(x,y)?.closest<HTMLElement>('[data-layer-drop-key]');
    if(!node||!list.contains(node)){layerDropRef.current=null;setLayerDrop(null);return;}
    let key=node.dataset.layerDropKey!;
    const targetGroup=key.startsWith('group:')?key.slice(6):null;
    const layer=state.layers.find(item=>item.id===key);
    let intoGroup=node.dataset.layerChild==='true'?layer?.editorGroup?.id??null:null;
    let ids=targetGroup?state.layers.filter(item=>item.editorGroup?.id===targetGroup).map(item=>item.id):[key];
    const completeGroup=pointer.ids.some(id=>{const group=state.layers.find(item=>item.id===id)?.editorGroup?.id;return group&&state.layers.filter(item=>item.editorGroup?.id===group).every(item=>pointer.ids.includes(item.id));});
    if(completeGroup&&intoGroup){key=`group:${intoGroup}`;ids=state.layers.filter(item=>item.editorGroup?.id===intoGroup).map(item=>item.id);intoGroup=null;}
    if(ids.some(id=>pointer.ids.includes(id))){layerDropRef.current=null;setLayerDrop(null);return;}
    const rect=node.getBoundingClientRect();const next={key,ids,intoGroup,after:y>rect.top+rect.height/2};
    layerDropRef.current=next;setLayerDrop(current=>current?.key===next.key&&current.after===next.after&&current.intoGroup===next.intoGroup?current:next);
  };
  const scrollLayers = () => {
    const pointer=layerPointer.current,list=layerListRef.current;if(!pointer?.active||!list)return;
    const rect=list.getBoundingClientRect(), before=list.scrollTop;
    if(pointer.py<rect.top+28)list.scrollTop-=10;else if(pointer.py>rect.bottom-28)list.scrollTop+=10;
    if(list.scrollTop!==before)updateLayerDrop(pointer.px,pointer.py);
    layerScrollFrame.current=requestAnimationFrame(scrollLayers);
  };
  const beginLayerPointer = (event:ReactPointerEvent<HTMLDivElement>,key:string,ids:string[],tap:()=>void,doubleTap:()=>void) => {
    if(event.button!==0||(event.target as Element).closest('button,input,textarea,select'))return;
    event.preventDefault();event.stopPropagation();
    if(readOnly||saving||ids.some(id=>lockedIds.has(id))){tap();return;}
    layerPointer.current={key,ids,x:event.clientX,y:event.clientY,px:event.clientX,py:event.clientY,pointerId:event.pointerId,active:false,tap,doubleTap};
    layerListRef.current?.setPointerCapture?.(event.pointerId);
  };
  const moveLayerPointer = (event:ReactPointerEvent<HTMLDivElement>) => {
    const pointer=layerPointer.current;if(!pointer)return;
    pointer.px=event.clientX;pointer.py=event.clientY;
    if(!pointer.active&&Math.hypot(event.clientX-pointer.x,event.clientY-pointer.y)<5)return;
    event.preventDefault();event.stopPropagation();
    if(!pointer.active){pointer.active=true;setDragLayerIds(pointer.ids);selectGroup(pointer.ids);layerListRef.current?.focus({preventScroll:true});layerScrollFrame.current=requestAnimationFrame(scrollLayers);}
    updateLayerDrop(event.clientX,event.clientY);
  };
  const finishLayerPointer = (event:ReactPointerEvent<HTMLDivElement>) => {
    const pointer=layerPointer.current;if(!pointer)return;event.preventDefault();event.stopPropagation();
    if(pointer.active){const target=layerDropRef.current;if(!readOnly&&!saving&&target)setState(current=>{const layers=dropNoticeLayers(current.layers,pointer.ids,target.ids,target.after,target.intoGroup);return layers===current.layers?current:{...current,layers};});}
    else{const now=performance.now();if(lastLayerTap.current.key===pointer.key&&now-lastLayerTap.current.time<350){pointer.doubleTap();lastLayerTap.current={key:'',time:0};}else{pointer.tap();lastLayerTap.current={key:pointer.key,time:now};}}
    cancelLayerPointer();
  };
  const pickMedia = (item: MediaItem) => {
    const kind = mediaPicker === 'video' || mediaPicker === 'webm' ? 'video' : 'image';
    const id = `${kind}-${Date.now().toString(36)}`;
    const maxZ = state.layers.length
      ? Math.max(...state.layers.map((l) => l.z))
      : 0;
    const box = nativeMediaBox(item, state.canvasW, state.canvasH);
    if (kind === 'image') {
      const l: NoticeImageLayer = {
        id,
        kind: 'image',
        ...box,
        z: maxZ + 1,
        opacity: 1,
        rotation: 0,
        mediaId: item.id,
        fit: 'contain',
        mediaScale: 1,
        mediaOffsetX: 0,
        mediaOffsetY: 0,
      };
      addLayer(l);
    } else if (kind === 'video') {
      const l: NoticeVideoLayer = {
        id,
        kind: 'video',
        ...box,
        z: maxZ + 1,
        opacity: 1,
        rotation: 0,
        mediaId: item.id,
        fit: 'contain',
        mediaScale: 1,
        mediaOffsetX: 0,
        mediaOffsetY: 0,
        loop: true,
        muted: true,
      };
      addLayer(l);
    }
    setMediaPicker(null);
  };

  // ---------------- save flow ----------------
  const stageElRef = useRef<HTMLDivElement | null>(null);

  // 대지 내보내기 — 지금 편집 중인 상태(저장 전 포함)를 대지 원본 크기 PNG로 내려받는다.
  const exportPng = async () => {
    setExporting(true);
    try {
      const blob = await renderThumbnail(state.layers, mediaById, state.canvasW, state.canvasH,
        state.canvasFill, state.canvasFillOpacity, weatherPreviews.current, [state.canvasW, state.canvasH]);
      if (!blob) throw new Error('rasterize failed');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${state.name?.trim() || '슬라이드'}_${state.canvasW}x${state.canvasH}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast('이미지를 만들지 못했습니다. 다시 시도해 주세요.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const save = async (meta: {
    name: string;
    durationSec: number;
  }) => {
    if (saving || readOnly) return;
    setSaving(true);
    // 템플릿 그래픽은 지금 라이브러리에 올리고 진짜 미디어 id 로 바꿔 끼운다 — 서버에는 "asset:" id 가 가지 않는다.
    let resolvedLayers = state.layers;
    if (state.layers.some((layer) => 'mediaId' in layer && assetForMediaId(layer.mediaId))) {
      try {
        resolvedLayers = (await resolveStationCTemplateAssets({ layers: state.layers }, mediaList, (file) => uploadTemplateMedia(file))).layers;
        setState((current) => ({ ...current, layers: resolvedLayers }));
      } catch (e) {
        setSaving(false);
        toast(`템플릿 그래픽을 라이브러리에 올리지 못해 저장하지 않았습니다 — ${describeApiError(e)}`, 'error');
        return;
      }
    }
    if (saveTarget) {
      try {
        await saveTarget.save({ name: meta.name, canvasWidth: state.canvasW, canvasHeight: state.canvasH, canvasFill: state.canvasFill,
          canvasFillOpacity: state.canvasFillOpacity, fitMode: state.canvasFitMode, layers: resolvedLayers });
      } catch (e) {
        setSaving(false);
        toast(`저장 실패 — ${describeApiError(e)}`, 'error');
        return;
      }
      setSaving(false);
      toast(`${saveTarget.label}에 저장되었습니다.`, 'success');
      onClose();
      return;
    }
    let savedId: string | null = null;
    let savedNotice: Notice | null = null;
    // --- Step 1: persist name + layers (source of truth). ---
    try {
      const layers = resolvedLayers;
      if (!notice?.id) {
        const created = await createNotice({
          name: meta.name,
          canvasWidth: state.canvasW,
          canvasHeight: state.canvasH,
          canvasFill: state.canvasFill,
          canvasFillOpacity: state.canvasFillOpacity,
          fitMode: state.canvasFitMode,
          durationSec: meta.durationSec,
          layers,
          ...(ownerGroupId !== undefined ? { ownerGroupId } : {}),
        });
        savedId = created.id;
      } else {
        await updateNotice(notice.id, {
          name: meta.name,
          canvasWidth: state.canvasW,
          canvasHeight: state.canvasH,
          canvasFill: state.canvasFill,
          canvasFillOpacity: state.canvasFillOpacity,
          fitMode: state.canvasFitMode,
          durationSec: meta.durationSec,
          layers,
        });
        savedId = notice.id;
        savedNotice = useLibraryStore.getState().notices.find((n) => n.id === notice.id) ?? null;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[notice] save core failed', e);
      setSaving(false);
      // 실패 알림은 앱 전역 토스트로 통일(2026-09-16 검수 S19).
      toast(`저장 실패 — ${describeApiError(e)}`, 'error');
      return;
    }

    // --- Step 2: best-effort thumbnail. Failures here must NOT block save. ---
    if (savedId) {
      try {
        const blob = await renderThumbnail(
          state.layers,
          mediaById,
          state.canvasW,
          state.canvasH,
          state.canvasFill,
          state.canvasFillOpacity,
          weatherPreviews.current,
        );
        if (blob) await uploadThumbnail(savedId, blob);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[notice] thumbnail upload failed (notice still saved)', e);
      }
    }

    setSaving(false);
    // 언제·어디에 반영되는지까지 알린다(SCDS-116). 송출 여부는 저장 응답(scheduled) 기준.
    const message = savedNotice ? slideSaveMessage(savedNotice, useLibraryStore.getState().notices, notice?.layers ?? null, mediaById)
      : { text: '저장되었습니다.', kind: 'success' as const };
    toast(message.text, message.kind);
    onClose();
  };

  const filteredMediaList =
    mediaPicker === 'webm'
      ? pickableMediaList.filter((m) => isWebmMedia(m))
    : mediaPicker === 'video'
      ? pickableMediaList.filter((m) => m.mediaType === 'video')
      : mediaPicker === 'image'
        ? pickableMediaList.filter((m) => m.mediaType === 'image')
        : pickableMediaList;

  // 텍스트 레이어 추가 — 최상단 z에 배치.
  const addTextLayer = () => {
    const id = `text-${Date.now().toString(36)}`;
    const maxZ = state.layers.length ? Math.max(...state.layers.map((l) => l.z)) : 0;
    addLayer({ ...textLayer('new', '새 텍스트', 480, 140, 60, 500), id, z: maxZ + 1 });
  };
  // 도형 추가 — 대지 가운데·최상단 z. 라인은 선만, 나머지는 채우기만(Figma 기본 회색).
  const addShapeLayer = (shape: NoticeShapeKind) => {
    const maxZ = state.layers.length ? Math.max(...state.layers.map((l) => l.z)) : 0;
    const size = Math.round(Math.min(state.canvasW, state.canvasH) / 4), line = shape === 'line';
    const width = line ? size * 2 : shape === 'speechBubble' ? Math.round(size * 1.4) : size;
    const height = line ? Math.max(8, Math.round(size / 8)) : size;
    addLayer({
      id: `shape-${crypto.randomUUID()}`, kind: 'shape', shape,
      x: Math.round((state.canvasW - width) / 2), y: Math.round((state.canvasH - height) / 2), width, height,
      z: maxZ + 1, opacity: 1, rotation: 0,
      fill: line ? null : '#D9D9D9', stroke: line ? '#D9D9D9' : null, strokeWidth: line ? 8 : 0,
      ...(shape === 'speechBubble' ? { cornerRadius: 40 } : {}),
    });
  };

  const shortcutsBlocked = !!(mediaPicker || saveModalOpen || dataWidgetEditor || aiDraftOpen || gridPresetOpen || stationCTemplateOpen || keyboardHelpOpen || resetConfirmOpen);
  // 대기 화면은 durationSec 0(무한 표시)이다. 템플릿·AI 초안 빌더는 1초 이상만 받으므로 미리보기용 장면 길이만
  // 클램프한다 — 적용 시 layers/canvasFill만 가져오므로 실제 durationSec은 바뀌지 않는다(2026-09-22 기기 대기 화면 편집기 공백 화면 원인).
  const templateDurationSec = Number.isFinite(state.durationSec) && state.durationSec >= 1 ? state.durationSec : 1;
  useEffect(() => {
    let active = true;
    const blocked = (event: Event) => event.defaultPrevented || isNoticeTextInput(event.target) || shortcutsBlocked || saving;
    const writable = !readOnly && state.hasCanvas;
    const editable = selectedLayers.filter(layer => !lockedIds.has(layer.id));
    const deleteSelected = () => {
      const removing = new Set(editable.map(layer => layer.id));
      if (!removing.size) return;
      setState(current => {
        const ids = idsFor(current).filter(id => !removing.has(id));
        return { ...current, layers: current.layers.filter(layer => !removing.has(layer.id)), selectedId: ids.at(-1) ?? null, selectedIds: ids };
      });
    };
    const insert = (source: NoticeLayer[], offset = 0) => {
      // A copied reference must still be available in the destination editor.
      if (source.some(layer => ('mediaId' in layer && !mediaById.has(layer.mediaId)) ||
        (layer.kind === 'dataWidget' && isBoWidgetKind(layer.source.kind) && !isBo))) {
        toast('사용할 수 없는 미디어나 위젯이 포함돼 있어 붙여넣을 수 없어요.', 'error'); return;
      }
      const additions = cloneNoticeLayers(source, Math.max(0, ...state.layers.map(layer => layer.z)), offset);
      if (!additions.length) return;
      setState(current => ({ ...current, layers: [...current.layers, ...additions], selectedId: additions.at(-1)!.id, selectedIds: additions.map(layer => layer.id) }));
    };
    const onClipboard = (event: ClipboardEvent) => {
      if (blocked(event) || !event.clipboardData) return;
      nudgeHistoryRef.current = null;
      if (event.type === 'paste') {
        if (!writable) return;
        const layers = readNoticeClipboard(event.clipboardData.getData('text/plain'), false);
        if (layers) { event.preventDefault(); insert(layers); }
      } else {
        const source = event.type === 'cut' ? editable : selectedLayers;
        if (!source.length || (event.type === 'cut' && !writable)) return;
        event.clipboardData.setData('text/plain', copyNoticeLayers(source));
        event.preventDefault();
        if (event.type === 'cut') deleteSelected();
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (blocked(event) || event.isComposing || event.keyCode === 229) return;
      if(layerPointer.current?.active){if(event.key==='Escape'){event.preventDefault();cancelLayerPointer();}return;}
      const mod = event.metaKey || event.ctrlKey;
      // Physical letter codes also work while the Korean input source is selected.
      const key = /^Key[A-Z]$/.test(event.code) ? event.code.slice(3).toLowerCase() : event.key.toLowerCase();
      if (mod && key === 'v') {
        if (event.shiftKey && writable) {
          // macOS browsers do not consistently emit paste for Cmd+Shift+V.
          event.preventDefault();
          if (!event.repeat) {
            if (!navigator.clipboard?.readText) toast('이 브라우저에서는 제자리 붙여넣기를 지원하지 않아요.', 'error');
            else void navigator.clipboard.readText().then(token => {
              if (!active) return;
              const layers = readNoticeClipboard(token, true);
              if (layers) { nudgeHistoryRef.current = null; insert(layers); }
            }).catch(() => { if (active) toast('제자리 붙여넣기를 사용하려면 브라우저의 클립보드 읽기를 허용해 주세요.', 'error'); });
          }
        }
        return;
      }
      if (mod && ['c', 'x'].includes(key)) return; // Native clipboard events own these keys.
      if (!key.startsWith('arrow')) nudgeHistoryRef.current = null;
      if (event.altKey) return;
      if (key === 'escape') {
        event.preventDefault();
        if (selectedIds.length) setState(current => ({ ...current, selectedId: null, selectedIds: [] }));
        else if (tool !== 'select') setTool('select');
        else onClose();
      } else if (mod && key === 'a') {
        event.preventDefault();
        const ids = state.layers.filter(layer => !layer.locked && !layer.hidden).map(layer => layer.id);
        setState(current => ({ ...current, selectedId: ids.at(-1) ?? null, selectedIds: ids }));
      } else if (event.key === '?' && !mod) { event.preventDefault(); setKeyboardHelpOpen(true); }
      else if (!mod && !event.shiftKey && key === 'v') { event.preventDefault(); setTool('select'); }
      else if (!mod && !event.shiftKey && key === 'h') { event.preventDefault(); setTool('move'); }
      else if (!mod && event.shiftKey && (event.code === 'Digit1' || event.key === '1' || event.key === '!')) { event.preventDefault(); setZoom(1); }
      else if (mod && key === 's') {
        event.preventDefault();
        if (writable && !hasInvalidRoomCard && !hasInvalidRoomTemplate) {
          if (saveTarget) void save({ name: state.name, durationSec: state.durationSec }); else setSaveModalOpen(true);
        }
      } else if (!writable) return;
      else if (mod && key === 'z') { event.preventDefault(); event.shiftKey ? redo() : undo(); }
      else if (mod && key === 'y') { event.preventDefault(); redo(); }
      else if (mod && key === 'g') { event.preventDefault(); if(!event.repeat)groupSelection(event.shiftKey); }
      else if (mod && key === 'd') { event.preventDefault(); if (!event.repeat) insert(editable, 16); }
      else if (!mod && event.shiftKey && key === 'g') { event.preventDefault(); if (!event.repeat) setLayoutGrid(grid => ({ ...grid, visible: !grid.visible })); }
      else if (!mod && !event.shiftKey && key === 't') { event.preventDefault(); if (!event.repeat) addTextLayer(); }
      else if (!mod && (key === 'delete' || key === 'backspace')) { event.preventDefault(); deleteSelected(); }
      else if (!mod && ['arrowleft', 'arrowright', 'arrowup', 'arrowdown'].includes(key)) {
        if (!editable.length) return;
        event.preventDefault();
        if (!event.repeat || !nudgeHistoryRef.current) nudgeHistoryRef.current = { recorded: false };
        const step = event.shiftKey ? 10 : 1;
        const dx = key === 'arrowleft' ? -step : key === 'arrowright' ? step : 0;
        const dy = key === 'arrowup' ? -step : key === 'arrowdown' ? step : 0;
        setState(current => ({ ...current, layers: current.layers.map(layer => selectedIds.includes(layer.id) && !lockedIds.has(layer.id) ? { ...layer, x: layer.x + dx, y: layer.y + dy } : layer) }));
      } else if (['[', ']', '{', '}'].includes(event.key) || ['BracketLeft', 'BracketRight'].includes(event.code)) {
        event.preventDefault();
        const front = event.code === 'BracketRight' || event.key === ']' || event.key === '}';
        setState(current => { const layers = orderNoticeLayers(current.layers, selectedIds, front ? 'front' : 'back', mod || event.shiftKey); return layers === current.layers ? current : { ...current, layers }; });
      }
    };
    const release = () => { nudgeHistoryRef.current = null; };
    document.addEventListener('keydown', onKey);
    document.addEventListener('keyup', release);
    window.addEventListener('blur', release);
    for (const type of ['copy', 'cut', 'paste']) document.addEventListener(type, onClipboard as EventListener);
    return () => {
      active = false;
      document.removeEventListener('keydown', onKey); document.removeEventListener('keyup', release); window.removeEventListener('blur', release);
      for (const type of ['copy', 'cut', 'paste']) document.removeEventListener(type, onClipboard as EventListener);
    };
  });

  // 이동 도구: 스테이지를 드래그해 스크롤(팬)한다. 선택 도구일 땐 무시.
  const onStagePanStart = (e: ReactPointerEvent) => {
    if (tool !== 'move') return;
    const area = canvasStageRef.current;
    if (!area) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const sl = area.scrollLeft;
    const st = area.scrollTop;
    const move = (ev: PointerEvent) => {
      area.scrollLeft = sl - (ev.clientX - startX);
      area.scrollTop = st - (ev.clientY - startY);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // 드래그로 개체를 고르는 마퀴 — 대지 안뿐 아니라 **작업 영역 어디서나** 시작한다
  // (2026-09-18 대교). 예전에는 대지 크기 div 에만 달려 있어서, 대지 밖 회색 영역에서
  // 끌기 시작하면 아무 일도 일어나지 않았다. 좌표는 대지 기준으로 재므로 대지 밖에서
  // 시작해도 음수 좌표로 자연스럽게 이어진다.
  const startMarquee = (event: ReactPointerEvent): void => {
    if (tool !== 'select' || event.button !== 0) return;
    // 레이어를 누른 것이면 그건 이동 드래그다 — 마퀴가 가로채지 않는다.
    if ((event.target as HTMLElement | null)?.closest('[data-notice-layer]')) return;
    const rect = stageBoxRef.current?.getBoundingClientRect();
    if (!rect) return;
    marqueeMoved.current = false;
    marqueeStart.current = {
      x: (event.clientX - rect.left) / displayScale,
      y: (event.clientY - rect.top) / displayScale,
      keep: event.shiftKey ? selectedIds : [],
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const moveMarquee = (event: ReactPointerEvent): void => {
    const start = marqueeStart.current; if (!start) return;
    const rect = stageBoxRef.current?.getBoundingClientRect(); if (!rect) return;
    const x = (event.clientX - rect.left) / displayScale, y = (event.clientY - rect.top) / displayScale;
    if (Math.hypot(x - start.x, y - start.y) * displayScale < 4 && !marqueeMoved.current) return;
    marqueeMoved.current = true;
    const box = { x: Math.min(x, start.x), y: Math.min(y, start.y), width: Math.abs(x - start.x), height: Math.abs(y - start.y) };
    setMarquee(box);
    setState(s => { const ids = expandNoticeGroupIds(s.layers, [...new Set([...start.keep, ...s.layers.filter(layer => {
      const b = layerBounds(layer); return !layer.hidden && !layer.locked && b.x < box.x + box.width && b.x + b.width > box.x && b.y < box.y + box.height && b.y + b.height > box.y;
    }).map(layer => layer.id)])]); return { ...s, selectedId: ids.at(-1) ?? null, selectedIds: ids }; });
  };
  const endMarquee = (): void => { marqueeStart.current = null; setMarquee(null); };
  const cancelMarquee = (): void => { marqueeStart.current = null; marqueeMoved.current = false; setMarquee(null); };

  const renderLayerRow = (l:NoticeLayer, child=false) => (
                  <div
                    key={l.id}
                    data-layer-row={l.id}
                    title={l.name || layerLabel(l)}
                    role="button"
                    tabIndex={0}
                    draggable={false}
                    data-layer-drop-key={l.id}
                    data-layer-child={child?'true':undefined}
                    onPointerDown={event=>beginLayerPointer(event,l.id,child?[l.id]:selectedIds.includes(l.id)?expandNoticeGroupIds(state.layers,selectedIds):[l.id],()=>selectLayer(l.id,event.shiftKey||event.metaKey||event.ctrlKey,false,child),()=>startRename(l))}
                    onClick={(event) => selectLayer(l.id, event.shiftKey || event.metaKey || event.ctrlKey,false,child)}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      startRename(l);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        selectLayer(l.id, e.shiftKey || e.metaKey || e.ctrlKey,false,child);
                      }
                    }}
                    className={cn(
                      'group relative w-full touch-none select-none flex items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-panel-2 transition-colors',
                      'cursor-grab active:cursor-grabbing', child&&'!pl-8',
                      selectedIds.includes(l.id) && 'bg-accent-soft text-accent',
                      l.hidden && 'opacity-45',
                      dragLayerIds.includes(l.id) && 'opacity-40 bg-panel-2',
                    )}
                  >
                    {layerDrop?.key===l.id && <span aria-hidden="true" className={`pointer-events-none absolute inset-x-0 z-10 border-t-2 border-accent ${layerDrop.after?'bottom-0':'top-0'}`}/>}
                    <LayerKindIcon kind={l.kind} {...(l.kind === 'dataWidget' ? { sourceKind: l.source.kind } : {})} />
                    {renamingId === l.id ? (
                      <input
                        autoFocus
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') commitRename(l.id);
                          else if (e.key === 'Escape') setRenamingId(null);
                        }}
                        onBlur={() => commitRename(l.id)}
                        placeholder={layerLabel(l)}
                        className="flex-1 min-w-0 bg-panel border border-accent rounded px-1.5 py-0.5 font-mono text-[13px] text-text outline-none"
                      />
                    ) : (
                      <span className="flex-1 truncate">
                        {l.name?.trim() ? l.name : layerLabel(l)}
                      </span>
                    )}
                    {renamingId !== l.id && (
                      <div className="flex items-center gap-0.5 shrink-0">
                        {/* 표시/숨김 — 숨김일 땐 항상 보이고, 아니면 호버 시 노출 */}
                        <button
                          type="button"
                          disabled={readOnly||saving}
                          title={l.hidden ? '표시' : '숨김'}
                          onClick={(e) => {
                            e.stopPropagation();
                            patchLayer(l.id, { hidden: !l.hidden });
                          }}
                          className={cn(
                            'w-5 h-5 flex items-center justify-center text-text-dim hover:text-text opacity-0 group-hover:opacity-100',
                            l.hidden && 'opacity-100 text-text',
                          )}
                        >
                          <EyeIcon off={!!l.hidden} />
                        </button>
                        {/* 잠금 — 잠김일 땐 항상 보임 */}
                        <button
                          type="button"
                          disabled={readOnly||saving}
                          title={l.locked ? '잠금 해제' : '잠금'}
                          onClick={(e) => {
                            e.stopPropagation();
                            patchLayer(l.id, { locked: !l.locked });
                          }}
                          className={cn(
                            'w-5 h-5 flex items-center justify-center text-text-dim hover:text-text opacity-0 group-hover:opacity-100',
                            l.locked && 'opacity-100 text-accent',
                          )}
                        >
                          <LockIcon locked={!!l.locked} />
                        </button>
                        <button
                          type="button"
                          disabled={readOnly||saving||lockedIds.has(l.id)}
                          title="삭제"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeLayer(l.id);
                          }}
                          className="w-5 h-5 flex items-center justify-center text-text-dim hover:text-red opacity-0 group-hover:opacity-100"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
  );

  return (
    <WeatherPreviewContext.Provider value={weatherPreviews.current}>
    <div
      role="dialog"
      aria-modal="true"
      data-modal-backdrop="full"
      className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-6"
      {...backdropCloseProps(onClose)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-panel border border-line-strong rounded-md shadow-[0_20px_60px_rgba(0,0,0,0.6)] w-full max-w-[1680px] h-[88vh] flex flex-col overflow-hidden"
      >
        {/* Header — 취소 / 저장 (이름·재생시간·폴더는 저장 팝업으로 이동) */}
        <header className="flex items-center justify-between px-5 py-3 border-b border-line bg-panel-2 max-md:flex-wrap max-md:gap-2 max-md:px-3 max-md:py-2 [&_button]:whitespace-nowrap">
          <span className="font-display text-[16px] font-semibold text-text-dim truncate max-w-[50%] max-md:min-w-0 max-md:flex-1">
            {state.name?.trim() ? state.name : notice ? '슬라이드 편집' : '새 슬라이드'}
          </span>
          {/* 오른쪽: [내용 채우기 도구] | [마무리]. 도구와 취소·저장이 한 줄에 섞여 있어 구분되지 않던 것을 묶음·구분선으로 나눔(2026-09-16 대교). */}
          <div className="flex items-center gap-3 max-md:contents">
            {/* 순서 AI 초안 → 템플릿 → 화면 분할(2026-09-16 대교). '대기 화면' 버튼은 편집기에서 제외 — 게시는 슬라이드 카드 ⋮, 설치 지역은 날씨 위젯 설정에서. */}
            {/* 헤더 컨트롤은 높이 36px(h-9) 한 단계로 통일 — AI 초안/템플릿/화면 분할 33.2px,
                취소/저장 41.2px, 단축키 28px가 섞여 있어 기준선이 흔들렸다(2026-09-16 검수 C7).
                좌우 배치·순서는 대교 확정안 그대로 두고 높이·타깃 크기만 맞춘다. */}
            <div role="group" aria-label="내용 채우기" className="flex items-center gap-1 rounded-md border border-line bg-panel p-1 max-md:order-last max-md:w-full max-md:overflow-x-auto">
              {/* 11px·text-dimmer는 흰 배경 대비 3.45:1로 AA 미달 → 12px·text-dim(약 6.9:1) (검수 C2). */}
              <span className="px-1.5 font-mono text-[12px] uppercase tracking-monoWide text-text-dim max-md:hidden">채우기</span>
              <Button className="!h-9 !py-0" onClick={() => setAiDraftOpen(true)} disabled={saving || readOnly}>AI 초안</Button>
              <Button className="!h-9 !py-0" onClick={() => setStationCTemplateOpen(true)} disabled={saving || readOnly}>템플릿</Button>
              <Button className="!h-9 !py-0" onClick={() => setGridPresetOpen(true)} disabled={saving || readOnly}>화면 분할</Button>
              <span aria-hidden="true" className="mx-0.5 h-5 w-px bg-line" />
              <button type="button" title="단축키 (?)" aria-label="단축키 안내" onClick={() => setKeyboardHelpOpen(true)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-text-dim hover:bg-panel-2 hover:text-text">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><rect x="2" y="5" width="20" height="14" rx="3" /><path d="M6 9h1m3 0h1m3 0h1m3 0h.5M6 12h1m3 0h1m3 0h1m3 0h.5M7 15h10" /></svg>
              </button>
            </div>
            <span aria-hidden="true" className="h-6 w-px bg-line-strong max-md:hidden" />
            <div className="flex items-center gap-2 max-md:flex-none">
              <Button className="!h-9 !py-0" onClick={onClose}>취소</Button>
              <Button
                // 폭 고정 — '저장'↔'저장 중…'으로 라벨이 바뀌어도 옆의 취소가 밀리지 않게.
                className="!h-9 min-w-[88px] justify-center !py-0"
                variant="primary"
                onClick={() => saveTarget ? void save({ name: state.name, durationSec: state.durationSec }) : setSaveModalOpen(true)}
                disabled={saving || readOnly || hasInvalidRoomCard || hasInvalidRoomTemplate}
              >
                {saving ? '저장 중…' : saveTarget ? `${saveTarget.label}에 저장` : '저장'}
              </Button>
            </div>
          </div>
        </header>

        {notice?.layers.some(layer => layer.kind === 'dataWidget' && layer.source.kind === 'clock' && (layer.source.showDate || layer.source.showWeekday)) && <p className="border-b border-line px-5 py-2 text-[12px] text-text-dim">기존 시계의 날짜·요일을 별도 위젯으로 분리했습니다. 저장하면 재생 화면에 반영됩니다.</p>}
        {/* Body — 레이어(좌) · 스테이지(중앙) · 속성(우). 툴바는 스테이지 하단 플로팅. */}
        {isMobile && <div className="flex flex-none justify-center border-b border-line bg-panel px-3 py-2">
          <Segmented value={mobilePane} onChange={setMobilePane}
            options={[{ value: 'canvas', label: '캔버스' }, { value: 'layers', label: `레이어 ${state.layers.length}` }, { value: 'props', label: '속성' }]} />
        </div>}
        <div className={cn('flex-1 min-h-0 grid', isMobile ? 'grid-cols-1' : 'grid-cols-[220px_1fr_300px]')}>
          {/* Left: Layers panel */}
          <aside className={cn('border-r border-line bg-panel flex flex-col min-h-0 overflow-hidden', isMobile && mobilePane !== 'layers' && 'hidden')}>
            <div className="px-3 py-2 border-b border-line font-mono text-[12px] uppercase tracking-monoWide text-text-dim">
              <div className="flex items-center justify-between gap-1"><span>레이어 ({state.layers.length})</span><div className="flex gap-1">
                <button type="button" aria-label="그룹화" title="그룹화 (Cmd/Ctrl+G)" disabled={groupActionBlocked||selectedLayers.length<2||wholeGroup} onClick={()=>groupSelection()} className="rounded border border-line px-1.5 py-1 text-[11px] disabled:opacity-30">그룹</button>
                <button type="button" aria-label="그룹 해제" title="그룹 해제 (Cmd/Ctrl+Shift+G)" disabled={groupActionBlocked||!selectedLayers.some(layer=>layer.editorGroup)} onClick={()=>groupSelection(true)} className="rounded border border-line px-1.5 py-1 text-[11px] disabled:opacity-30">해제</button>
              </div></div>
              <p className="mt-1 text-[10px] normal-case text-text-dimmer">드래그로 순서 변경 · 위쪽이 앞쪽</p>
            </div>
            <div data-layer-list ref={layerListRef} tabIndex={-1} onPointerMove={moveLayerPointer} onPointerUp={finishLayerPointer} onPointerCancel={cancelLayerPointer} className="flex-1 overflow-auto py-1 outline-none">
              {noticeLayerUnits(state.layers).map(unit=>unit.groupId ? <div key={unit.id} className="relative" data-layer-group={unit.groupId}>
                {layerDrop?.key===unit.id && <span aria-hidden="true" className={`pointer-events-none absolute inset-x-0 z-10 border-t-2 border-accent ${layerDrop.after?'bottom-0':'top-0'}`}/>}
                <div role="button" tabIndex={0} title="더블클릭으로 그룹 이름 변경" className={cn('flex min-h-9 touch-none select-none cursor-grab items-center gap-1 border-b border-line px-2 text-[13px]',unit.layers.every(layer=>selectedIds.includes(layer.id))?'bg-accent-soft text-accent':'bg-panel-2')}
                  draggable={false} data-layer-drop-key={unit.id}
                  onPointerDown={event=>beginLayerPointer(event,unit.id,unit.layers.map(layer=>layer.id),()=>selectGroup(unit.layers.map(layer=>layer.id),event.shiftKey||event.metaKey||event.ctrlKey),()=>{if(!readOnly&&!saving&&!unit.layers.some(layer=>lockedIds.has(layer.id))){setRenamingId(unit.id);setRenameDraft(unit.name??'그룹');}})}
                  onClick={event=>selectGroup(unit.layers.map(layer=>layer.id),event.shiftKey||event.metaKey||event.ctrlKey)}
                  onKeyDown={event=>{if(event.key==='Enter'){event.preventDefault();selectGroup(unit.layers.map(layer=>layer.id));}}}
                  onDoubleClick={()=>{if(!readOnly&&!saving&&!unit.layers.some(layer=>lockedIds.has(layer.id))){setRenamingId(unit.id);setRenameDraft(unit.name??'그룹');}}}>
                  <button type="button" aria-label={`${unit.name} ${collapsedGroups.has(unit.groupId!)?'펼치기':'접기'}`} aria-expanded={!collapsedGroups.has(unit.groupId!)} className="h-6 w-5 shrink-0" onClick={event=>{event.stopPropagation();setCollapsedGroups(current=>{const next=new Set(current);next.has(unit.groupId!)?next.delete(unit.groupId!):next.add(unit.groupId!);return next;});}}>{collapsedGroups.has(unit.groupId)?'›':'⌄'}</button>
                  <span aria-hidden="true">▱</span>
                  {renamingId===unit.id?<input autoFocus aria-label="그룹 이름" maxLength={80} className="min-w-0 flex-1 rounded border border-accent bg-panel px-1" value={renameDraft} onClick={event=>event.stopPropagation()} onChange={event=>setRenameDraft(event.target.value)}
                    onKeyDown={event=>{event.stopPropagation();if(event.key==='Escape')setRenamingId(null);if(event.key==='Enter')event.currentTarget.blur();}}
                    onBlur={()=>{const name=renameDraft.trim()||'그룹';setState(current=>({...current,layers:current.layers.map(layer=>layer.editorGroup&&layer.editorGroup.id===unit.groupId?{...layer,editorGroup:{...layer.editorGroup,name}}:layer)}));setRenamingId(null);}}/>
                    :<span className="min-w-0 flex-1 truncate">{unit.name}</span>}
                  <span className="text-[11px] text-text-dim">{unit.layers.length}</span>
                  <button type="button" aria-label={`${unit.name} ${unit.layers.some(layer=>layer.locked)?'잠금 해제':'잠금'}`} disabled={readOnly||saving} className="h-6 w-6" onClick={event=>{event.stopPropagation();const locked=!unit.layers.some(layer=>layer.locked);setState(current=>({...current,layers:current.layers.map(layer=>layer.editorGroup?.id===unit.groupId?{...layer,locked}:layer)}));}}><LockIcon locked={unit.layers.some(layer=>layer.locked)}/></button>
                </div>
                {!collapsedGroups.has(unit.groupId)&&unit.layers.map(layer=>renderLayerRow(layer,true))}
              </div> : renderLayerRow(unit.layers[0]!))}
            </div>
          </aside>

          {/* Stage — 스크롤 컨테이너(줌 시 오버플로 스크롤/이동도구 팬).
              하단 중앙 플로팅 툴바 + 우하단 줌 컨트롤이 이 셀 안에 절대배치. */}
          <div className={cn('relative min-w-0 min-h-0 flex flex-col', isMobile && mobilePane !== 'canvas' && 'hidden')}>
            <div
              ref={canvasStageRef}
              className={cn(
                // flex + m-auto on the box centers it AND still allows scrolling
                // to every edge when zoomed past the viewport (justify-center
                // would clip the top-left; margin:auto does not).
                'relative flex-1 min-h-0 bg-[#101114] overflow-auto p-6 flex',
                tool === 'move' && 'cursor-grab active:cursor-grabbing',
              )}
              onClick={() => {
                // 마퀴로 고른 직후의 클릭은 선택을 지우지 않는다 — 드래그 끝의 click 이 따라온다.
                if (marqueeMoved.current) { marqueeMoved.current = false; return; }
                if (tool === 'select') {
                  setState((s) => ({ ...s, selectedId: null, selectedIds: [] }));
                  setEditingId(null);
                }
              }}
              onPointerDown={event => { onStagePanStart(event); startMarquee(event); }}
              onPointerMove={moveMarquee}
              onPointerUp={endMarquee}
              onPointerCancel={cancelMarquee}
            >
              {state.hasCanvas && (
              <div
                ref={stageBoxRef}
                // 대지에서 넘어간 부분은 레이어마다 따로 잘라 낸다(아래 LayerView의 clipPath).
                // 대지 상자에 overflow-hidden 을 걸면 요소의 선택 테두리와 크기 조절 손잡이까지
                // 같이 잘려서, 화면 밖으로 밀린 요소를 다시 붙잡을 방법이 없었다(2026-09-17 대교).
                className="relative m-auto shrink-0 bg-black shadow-[0_0_0_1px_rgba(255,255,255,0.08)]"
                style={{
                  width: state.canvasW * displayScale,
                  height: state.canvasH * displayScale,
                }}
                onClick={(e) => {
                  // BRICK-105 — 대지를 직접 클릭하면 대지 속성으로 전환한다.
                  // 예전엔 전파만 막아서, 레이어를 고른 뒤 대지를 눌러도
                  // 대지 속성 패널로 돌아올 방법이 없었다(빈 여백 클릭만 가능).
                  e.stopPropagation();
                  if (marqueeMoved.current) { marqueeMoved.current = false; return; }
                  setState((s) => (s.selectedId === null ? s : { ...s, selectedId: null, selectedIds: [] }));
                }}
              >
                {/* Virtual canvas painted at natural size, then scaled uniformly.
                    top-left origin so (0,0) virtual aligns with (0,0) of box. */}
                <div
                  ref={stageElRef}
                  className="absolute top-0 left-0"
                  style={{
                    width: state.canvasW,
                    height: state.canvasH,
                    transform: `scale(${displayScale})`,
                    transformOrigin: 'top left',
                  }}
                >
                  {/* 대지 채우기 미리보기 — 모든 레이어 뒤 배경 */}
                  {state.canvasFill && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: state.canvasFill,
                        opacity: Math.max(0, Math.min(1, state.canvasFillOpacity / 100)),
                      }}
                    />
                  )}
                  {[...state.layers]
                    .sort((a, b) => a.z - b.z)
                    .filter((layer) => !layer.hidden)
                    .map((layer) => (
                      <LayerView
                        canvasWidth={state.canvasW} canvasHeight={state.canvasH}
                        key={layer.id}
                        layer={layer}
                        mediaById={mediaById}
                        selected={selectedIds.includes(layer.id)}
                        multiSelected={selectedIds.length > 1}
                        editing={editingId === layer.id}
                        dimmed={false}
                        locked={lockedIds.has(layer.id) || tool === 'move' || readOnly || saving}
                        aspectLocked={aspectLocks[layer.id] ?? layer.kind === 'dataWidget'}
                        scale={displayScale}
                        snapLines={snapLines}
                        onSelect={(additive, preserveGroup) => {
                          selectLayer(layer.id, additive, preserveGroup);
                          if (editingId && editingId !== layer.id) setEditingId(null);
                        }}
                        onStartEdit={() => {
                          if (layer.kind !== 'text' || layer.locked) return;
                          selectLayer(layer.id);
                          setEditingId(layer.id);
                        }}
                        onEndEdit={() => setEditingId(null)}
                        onPatch={(patch) => dragPatchLayer(layer.id, patch)}
                        onGesture={markGesture}
                      />
                    ))}
                  {selectedLayers.length > 1 && selectionBox && <div data-selection-bounds className="absolute border border-accent pointer-events-none" style={{ ...{ left: selectionBox.x, top: selectionBox.y, width: selectionBox.width, height: selectionBox.height }, borderWidth: 1 / displayScale, zIndex: Math.max(...state.layers.map(layer => layer.z)) + 1 }}>
                    {!groupActionBlocked && (['nw', 'ne', 'se', 'sw'] as const).map(dir => <ResizeHandle key={dir} dir={dir}
                      layer={{ ...selectedLayers[0]!, ...selectionBox, rotation: 0 }} scale={displayScale} aspectLocked={groupAspectLocked} onPatch={resizeGroup} onGesture={markGesture} />)}
                  </div>}
                  {marquee && <div className="absolute pointer-events-none border border-accent bg-accent/10" style={{ left: marquee.x, top: marquee.y, width: marquee.width, height: marquee.height, borderWidth: 1 / displayScale, zIndex: 100000 }} />}

                </div>
                {layoutGrid.visible && <LayoutGridOverlay grid={layoutGrid} width={state.canvasW} height={state.canvasH} scale={displayScale} />}
              </div>
              )}
            </div>

            {/* 하단 중앙 플로팅 툴바 — 이전/앞으로 · 도구 · 추가 */}
            <div className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center px-4 max-md:bottom-2 max-md:px-2">
              <div className="pointer-events-auto flex max-w-full items-center gap-1 overflow-x-auto rounded-lg border border-white/15 bg-black/80 px-2 py-1.5 shadow-lg backdrop-blur [&_span]:whitespace-nowrap">
                <ToolbarBtn onClick={undo} disabled={readOnly || saving || !canUndo} title="이전 단계">
                  <UndoIcon />
                </ToolbarBtn>
                <ToolbarBtn onClick={redo} disabled={readOnly || saving || !canRedo} title="앞으로">
                  <RedoIcon />
                </ToolbarBtn>
                <ToolbarDivider label="도구" />
                <ToolbarBtn
                  onClick={() => setTool('select')}
                  active={tool === 'select'}
                  title="선택"
                >
                  <CursorIcon />
                </ToolbarBtn>
                <ToolbarBtn
                  onClick={() => setTool('move')}
                  active={tool === 'move'}
                  title="이동(화면 이동)"
                >
                  <MoveIcon />
                </ToolbarBtn>
                <ToolbarDivider label="추가" />
                <ToolbarMenu glyph={<DataWidgetIcon />} title="위젯 추가" side="top"
                  disabled={saving || readOnly || !state.hasCanvas}
                  className="!h-8 !w-8 !border-0 !bg-transparent text-white/70 hover:text-white disabled:opacity-30">
                  {Object.entries(DATA_WIDGET_LABELS).filter(([kind]) => isBo || !isBoWidgetKind(kind as NoticeDataWidgetLayer['source']['kind'])).map(([kind, label]) =>
                    <button key={kind} role="menuitem" type="button" className="flex min-h-10 items-center gap-3 rounded px-3 py-2 text-left text-[13px] text-text hover:bg-panel-2 focus:bg-panel-2 focus:outline-none"
                      onClick={() => {
                        if (saving || readOnly || !state.hasCanvas) return;
                        const z = Math.max(0, ...state.layers.map(layer => layer.z)) + 1;
                        if (kind === 'clock') addLayer(defaultClockLayer(state.canvasW, state.canvasH, z, state.canvasFill));
                        else if (kind === 'date' || kind === 'weekday') addLayer(defaultDateLayer(state.canvasW, state.canvasH, z, kind, state.canvasFill));
                        else { setDataWidgetInitialKind(kind as NoticeDataWidgetLayer['source']['kind']); setDataWidgetEditor('new'); }
                      }}>
                      {kind === 'clock' ? <ClockWidgetIcon /> : kind === 'date' ? <DateWidgetIcon /> : kind === 'weekday' ? <WeekdayWidgetIcon /> : kind === 'weather' ? <WeatherWidgetIcon /> : <DataWidgetIcon />}
                      {label}
                    </button>)}
                </ToolbarMenu>
                <ToolbarBtn onClick={addTextLayer} disabled={!state.hasCanvas} title="텍스트 추가">
                  <span className="font-display text-[15px] font-bold leading-none">T</span>
                </ToolbarBtn>
                <ToolbarMenu glyph={<ShapesIcon />} title="도형 추가" side="top"
                  disabled={saving || readOnly || !state.hasCanvas}
                  className="!h-8 !w-8 !border-0 !bg-transparent text-white/70 hover:text-white disabled:opacity-30">
                  {(Object.keys(NOTICE_SHAPE_LABELS) as NoticeShapeKind[]).map((shape) =>
                    <button key={shape} role="menuitem" type="button" className="flex min-h-10 items-center gap-3 rounded px-3 py-2 text-left text-[13px] text-text hover:bg-panel-2 focus:bg-panel-2 focus:outline-none"
                      onClick={() => { if (!saving && !readOnly && state.hasCanvas) addShapeLayer(shape); }}>
                      <ShapeIcon shape={shape} />
                      {NOTICE_SHAPE_LABELS[shape]}
                    </button>)}
                </ToolbarMenu>
                <ToolbarBtn onClick={() => setMediaPicker('image')} disabled={!state.hasCanvas} title="이미지 추가">
                  <ImageIcon />
                </ToolbarBtn>
                <ToolbarBtn onClick={() => setMediaPicker('video')} disabled={!state.hasCanvas} title="영상 추가">
                  <VideoIcon />
                </ToolbarBtn>
                <ToolbarBtn onClick={() => setMediaPicker('webm')} disabled={!state.hasCanvas} title="WebM 추가">
                  <span className="text-[10px] font-mono font-semibold leading-none">WEBM</span>
                </ToolbarBtn>
                <ToolbarDivider />
                <ToolbarMenu glyph={<LayoutGridIcon />} title="레이아웃 그리드 (Shift G)" disabled={!state.hasCanvas}
                  className={cn('!h-8 !w-8 !border-0 !bg-transparent hover:text-white disabled:opacity-30', layoutGrid.visible ? 'text-accent' : 'text-white/70')}>
                  <LayoutGridSettings grid={layoutGrid} onChange={setLayoutGrid} />
                </ToolbarMenu>
                <ToolbarBtn onClick={() => setResetConfirmOpen(true)} disabled={readOnly || saving} title="초기화(편집 시작 시점으로)">
                  <ResetIcon />
                </ToolbarBtn>
              </div>
            </div>

            {/* 우하단 줌 컨트롤 — 라벨 클릭 시 100%(맞춤)로 리셋 */}
            {/* 모바일은 아래 툴바와 겹치지 않게 위쪽 오른편으로 올린다. */}
            <div className="pointer-events-none absolute right-4 bottom-4 flex justify-end max-md:bottom-auto max-md:right-2 max-md:top-2">
              <div className="pointer-events-auto flex items-center gap-0.5 rounded-lg border border-white/15 bg-black/80 px-1 py-1 shadow-lg backdrop-blur">
                <ToolbarBtn
                  onClick={() => setZoom((z) => Math.max(0.1, Math.round((z - 0.1) * 10) / 10))}
                  title="축소"
                >
                  <span className="text-[16px] leading-none">−</span>
                </ToolbarBtn>
                {/* 52x18.8이라 누르기 어려웠다 → 좌우 ToolbarBtn과 같은 32px 높이(검수 C1). */}
                <button
                  type="button"
                  onClick={() => setZoom(1)}
                  title="100%로 맞춤"
                  className="flex h-8 min-w-[52px] items-center justify-center px-1 text-center text-[13px] tabular-nums text-white/85 hover:text-accent"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <ToolbarBtn
                  onClick={() => setZoom((z) => Math.min(4, Math.round((z + 0.1) * 10) / 10))}
                  title="확대"
                >
                  <span className="text-[16px] leading-none">+</span>
                </ToolbarBtn>
              </div>
            </div>
          </div>

          {/* Right: Properties — 레이어 선택 시 해당 속성, 없으면 대지 속성. */}
          <aside className={cn('border-l border-line bg-panel flex flex-col min-h-0 overflow-hidden', isMobile && mobilePane !== 'props' && 'hidden')}>
            <div className="px-3 py-2 border-b border-line font-mono text-[12px] uppercase tracking-monoWide text-text-dim">
              속성
            </div>
            <div className="flex-1 overflow-auto p-3">
              {selectedLayers.length > 1 && selectionBox ? (
                <fieldset disabled={groupActionBlocked} className="space-y-4">
                  <InspectorTitle>{wholeGroup?selectedLayers[0]?.editorGroup?.name:`${selectedLayers.length}개 선택`}</InspectorTitle>
                  {selectionControls}
                  <PropSection title="선택 영역 위치"><div className="grid grid-cols-2 gap-2">
                    <NumField label="X" value={selectionBox.x} onChange={x => resizeGroup({ x })} />
                    <NumField label="Y" value={selectionBox.y} onChange={y => resizeGroup({ y })} />
                  </div></PropSection>
                  <PropSection title="선택 영역 크기"><SizeFields locked={groupAspectLocked} onLockedChange={setGroupAspectLocked} w={selectionBox.width} h={selectionBox.height} onChange={(width, height) => resizeGroup({ width, height })} /></PropSection>
                </fieldset>
              ) : selected ? (<>
                {selectionControls}
                <LayerProperties
                  layer={selected}
                  readOnly={readOnly || saving || lockedIds.has(selected.id)}
                  aspectLocked={aspectLocks[selected.id] ?? selected.kind === 'dataWidget'}
                  onAspectLockedChange={locked => setAspectLocks(current => ({ ...current, [selected.id]: locked }))}
                  onEditDataWidget={() => {
                    if (!readOnly && !saving && selected.kind === 'dataWidget' && !selected.locked) setDataWidgetEditor(selected.id);
                  }}
                  onPatch={(patch) => patchLayer(selected.id, patch)}
                />
              </>) : (
                <CanvasProperties
                  canvasW={state.canvasW}
                  canvasH={state.canvasH}
                  devices={sizedDevices}
                  pick={canvasPick}
                  onPick={setCanvasPick}
                  exporting={exporting}
                  onExport={() => void exportPng()}
                  fill={state.canvasFill}
                  fillOpacity={state.canvasFillOpacity}
                  fitMode={state.canvasFitMode}
                  onFitMode={canvasFitMode => { if (!readOnly && !saving) setState(s => ({ ...s, canvasFitMode })); }}
                  onSize={applyCanvasSize}
                  onFill={(fill, opacity) =>
                    setState((s) => ({
                      ...s,
                      canvasFill: fill,
                      canvasFillOpacity: opacity,
                    }))
                  }
                />
              )}
            </div>
          </aside>
        </div>
      </div>

      {dataWidgetEditor && !readOnly && (dataWidgetEditor === 'new' || editedWidget) && (
        <NoticeDataWidgetModal initialKind={dataWidgetInitialKind} isBo={isBo} canvasWidth={state.canvasW} canvasHeight={state.canvasH} layer={editedWidget ?? null}
          nextZ={Math.max(0, ...state.layers.map((layer) => layer.z)) + 1} onClose={() => setDataWidgetEditor(null)} onApply={(layer) => {
            if (readOnly || saving || !state.hasCanvas) return;
            if (dataWidgetEditor === 'new') addLayer(layer);
            else if (editedWidget && !editedWidget.locked) patchLayer(editedWidget.id, layer);
            setEditingId(null);
          }} />
      )}
      {/* 'AI 초안'은 같은 템플릿 갤러리의 AI 모드 — 새 템플릿 카드를 고르고 전달할 내용으로 문구를 채운다(2026-09-30). */}
      {(stationCTemplateOpen || aiDraftOpen) && !readOnly && (
        <StationCNoticeTemplateGallery key={aiDraftOpen ? 'ai' : 'template'} ai={aiDraftOpen ? { groupId: contentGroupId } : undefined}
          registeredTemplates={registeredTemplates} onDeleteTemplate={deleteNotice} isBo={isBo} pickableMedia={pickableMediaList} canvasWidth={state.canvasW} canvasHeight={state.canvasH} durationSec={templateDurationSec}
          layers={state.layers} mediaList={mediaList} onClose={() => { setStationCTemplateOpen(false); setAiDraftOpen(false); }} onApply={(draft) => {
            if (readOnly) return;
            setState((current) => ({ ...current, layers: draft.layers, canvasFill: draft.canvasFill,
              canvasFillOpacity: draft.canvasFillOpacity, selectedId: null, selectedIds: [] }));
            setEditingId(null);
          }} />
      )}
      {keyboardHelpOpen && <NoticeKeyboardHelp onClose={() => setKeyboardHelpOpen(false)} />}
      {resetConfirmOpen && (
        <ConfirmModal
          title="편집 초기화"
          message="편집 시작 시점으로 되돌릴까요? 저장 전 변경 사항이 사라집니다."
          confirmLabel="초기화"
          danger
          onCancel={() => setResetConfirmOpen(false)}
          onConfirm={() => resetToInitial()}
        />
      )}
      {gridPresetOpen && !readOnly && (
        <NoticeGridPresetModal canvasWidth={state.canvasW} canvasHeight={state.canvasH} canvasFill={state.canvasFill} canvasFillOpacity={state.canvasFillOpacity} isBo={isBo} layers={state.layers} mediaList={mediaList}
          onClose={() => setGridPresetOpen(false)} onApply={(layers) => {
            if (readOnly) return;
            // One state change feeds the existing Undo/Redo history and the ordinary Notice save path.
            setState((current) => ({ ...current, layers, selectedId: null, selectedIds: [] }));
            setEditingId(null);
          }} />
      )}
      {mediaPicker && (
        <TargetPickerModal
          kind="media"
          items={filteredMediaList}
          selectedId=""
          onSelect={(id) => {
            const m = mediaList.find((mm) => mm.id === id);
            if (m) pickMedia(m);
          }}
          onClose={() => setMediaPicker(null)}
        />
      )}

      {saveModalOpen && (
        <SlideSaveModal
          initialName={state.name}
          initialDurationSec={state.durationSec}
          saving={saving}
          onCancel={() => setSaveModalOpen(false)}
          onConfirm={(meta) => {
            setState((s) => ({ ...s, name: meta.name, durationSec: meta.durationSec }));
            setSaveModalOpen(false);
            void save(meta);
          }}
        />
      )}
    </div>
    </WeatherPreviewContext.Provider>
  );
}

// -----------------------------------------------------------------------------
// Layer view — draggable + resizable in the virtual canvas.
// -----------------------------------------------------------------------------
function LayerView({
  canvasWidth, canvasHeight,
  layer,
  mediaById,
  selected,
  multiSelected = false,
  editing,
  dimmed,
  locked,
  aspectLocked,
  scale,
  snapLines = null,
  onSelect,
  onStartEdit,
  onEndEdit,
  onPatch,
  onGesture,
}: {
  canvasWidth: number; canvasHeight: number;
  layer: NoticeLayer;
  /** 레이아웃 그리드가 보일 때의 선(대지 좌표). 있으면 Shift+드래그가 축 고정 대신 선에 붙인다. */
  snapLines?: { x: number[]; y: number[] } | null;
  mediaById: Map<string, MediaItem>;
  selected: boolean;
  multiSelected?: boolean;
  /** True when this text layer is being edited inline on the canvas. */
  editing: boolean;
  /** True when the scrubber is outside this layer's timeline window — shown
   *  as a faint ghost so it stays selectable/editable but reads as "off". */
  dimmed: boolean;
  /** Locked (padlock) or the move/pan tool is active — no drag/select/resize
   *  on the canvas. Still editable via the layer list + right panel. */
  locked: boolean;
  aspectLocked: boolean;
  scale: number;
  onSelect: (additive?: boolean, preserveGroup?: boolean) => void;
  /** Enter inline text edit (double-click on a text layer). */
  onStartEdit: () => void;
  /** Commit + leave inline text edit (blur / Escape). */
  onEndEdit: () => void;
  onPatch: (patch: Partial<NoticeLayer>) => void;
  /** 포인터 제스처의 시작·끝. 드래그 한 번을 되돌리기 한 번으로 묶는 데 쓴다. */
  onGesture: (phase: 'start' | 'end') => void;
}) {
  // 보이는 영역 = 대지 ∩ (분할 칸이 있으면 그 칸). 예전에는 대지 상자가 통째로 잘라 줘서
  // 분할 칸만 계산하면 됐는데, 그 상자의 overflow 를 푼 지금은 대지 경계도 여기서 잘라야 한다.
  // 자르는 건 내용뿐이고, 선택 테두리와 손잡이는 이 div 바깥이라 그대로 보인다.
  const cell = noticeGridClip(layer, canvasWidth, canvasHeight);
  const region = {
    x: Math.max(0, cell?.x ?? 0),
    y: Math.max(0, cell?.y ?? 0),
    right: Math.min(canvasWidth, cell ? cell.x + cell.width : canvasWidth),
    bottom: Math.min(canvasHeight, cell ? cell.y + cell.height : canvasHeight),
  };
  const inset = {
    top: Math.max(0, region.y - layer.y),
    right: Math.max(0, layer.x + layer.width - region.right),
    bottom: Math.max(0, layer.y + layer.height - region.bottom),
    left: Math.max(0, region.x - layer.x),
  };
  const clipPath = inset.top || inset.right || inset.bottom || inset.left
    ? `inset(${inset.top}px ${inset.right}px ${inset.bottom}px ${inset.left}px)` : undefined;
  const dragStartRef = useRef({ x: 0, y: 0 });
  const dragAxisRef = useRef<'x' | 'y' | null>(null);
  const bindDrag = useDrag(
    ({ first, last, tap, movement: [mx, my], shiftKey, event }) => {
      if (locked) return;
      event.stopPropagation();
      if (first) onGesture('start');
      if (last) onGesture('end');
      // Pure click/tap (movement under threshold): select only, never move.
      // filterTaps still fires the handler once for a tap with first=false
      // and movement [0,0]; without this guard the patch below would write
      // the stale dragStartRef (0,0) and snap the layer to the origin.
      if (tap) return;
      if (first) {
        onSelect(false, true);
        dragStartRef.current = { x: layer.x, y: layer.y };
        dragAxisRef.current = null;
      }
      const snap = shiftKey && snapLines;
      if (!shiftKey || snap) dragAxisRef.current = null;
      else if (dragAxisRef.current === null && (mx !== 0 || my !== 0)) {
        dragAxisRef.current = Math.abs(mx) >= Math.abs(my) ? 'x' : 'y';
      }
      let x = Math.round(dragStartRef.current.x + mx / Math.max(0.01, scale)), y = Math.round(dragStartRef.current.y + my / Math.max(0.01, scale));
      if (snap) {
        // 보이는 상자(회전 포함)의 가장자리를 선에 붙이고, 같은 만큼 레이어 좌표를 옮긴다.
        const box = layerBounds({ ...layer, x, y });
        x = Math.round((snapToLines(box.x, box.width, snap.x) - box.x + x) * 100) / 100;
        y = Math.round((snapToLines(box.y, box.height, snap.y) - box.y + y) * 100) / 100;
      }
      // Lock in canvas coordinates, before the existing group-move delta is applied.
      onPatch({ ...(dragAxisRef.current !== 'y' ? { x } : {}), ...(dragAxisRef.current !== 'x' ? { y } : {}) });
    },
    { filterTaps: true, threshold: 3 },
  );

  // 편집 중에는 드래그/선택을 비활성화해 텍스트 입력·캐럿·IME가 정상 동작하도록 한다.
  const dragEnabled = !locked && !editing;
  // 레이어 내용은 두 번 그린다 — 대지 안(선명)과 대지 밖(흐림). 아래 렌더 참고.
  const body = (
    <>
        {layer.kind === 'text' ? (
          <TextLayerBody
            layer={layer}
            editing={editing}
            onCommit={(text) => onPatch({ text } as Partial<NoticeLayer>)}
            onEndEdit={onEndEdit}
          />
        ) : null}
        {layer.kind === 'dataWidget' ? (
          <LiveWidgetBody layer={layer} />
        ) : null}
        {layer.kind === 'shape' ? <ShapeLayerBody layer={layer} /> : null}
        {layer.kind === 'image' ? (
          <ImageLayerBody layer={layer} media={mediaById.get(layer.mediaId)} />
        ) : null}
        {layer.kind === 'video' ? (
          <VideoLayerBody layer={layer} media={mediaById.get(layer.mediaId)} />
        ) : null}
        {layer.kind === 'lottie' ? (
          <LottieLayerBody layer={layer} media={mediaById.get(layer.mediaId)} />
        ) : null}
        {layer.kind === 'brickBrief' ? <BrickBriefLayerBody layer={layer} /> : null}
        {layer.kind === 'roomStatus' ? <RoomStatusLayerBody layer={layer} /> : null}
        {layer.kind === 'roomCard' ? <RoomCardLayerBody layer={layer} /> : null}
        {layer.kind === 'roomTemplate' ? <RoomTemplateLayerBody layer={layer} /> : null}
    </>
  );
  return (
    <div
      {...(dragEnabled ? (bindDrag() as object) : {})}
      data-notice-layer={layer.id}
      onClick={(e) => {
        if (locked || editing) return; // 잠금/이동도구/편집 중 — 캔버스 클릭 선택 차단
        e.stopPropagation();
        onSelect(e.shiftKey || e.metaKey || e.ctrlKey);
      }}
      onDoubleClick={(e) => {
        if (locked || layer.kind !== 'text') return;
        e.stopPropagation();
        onStartEdit();
      }}
      className={cn(
        'absolute touch-none',
        selected ? 'outline outline-accent' : 'outline outline-transparent hover:outline-white/40',
      )}
      style={{
        // 선택 테두리도 화면 px 기준(선택 2px·간격 2px, 마우스 올림 1px).
        outlineWidth: (selected ? 2 : 1) / Math.max(0.01, scale),
        outlineOffset: selected ? 2 / Math.max(0.01, scale) : 0,
        left: layer.x,
        top: layer.y,
        width: layer.width,
        height: layer.height,
        opacity: dimmed ? 0.12 : layer.opacity,
        zIndex: layer.z,
        cursor: locked ? 'default' : editing ? 'text' : 'move',
        pointerEvents: locked ? 'none' : undefined,
      }}
    >
      {/* 대지 밖으로 나간 부분은 잘라 내되 흐리게 남긴다 — 완전히 지우면 요소가 밖에 있는지조차
          알 수 없어 다시 붙잡을 단서가 없다(2026-09-18 대교). 클릭·드래그는 이 div 바깥의 레이어
          상자가 받으므로 여기서는 포인터를 꺼 둔다. */}
      {clipPath && <div aria-hidden className="absolute inset-0 opacity-25 pointer-events-none">{body}</div>}
      <div className="absolute inset-0" style={{ clipPath }}>{body}</div>
      {selected && !multiSelected && !locked && !editing && (
        <>
          <ResizeHandle dir="nw" layer={layer} scale={scale} aspectLocked={aspectLocked} onPatch={onPatch} onGesture={onGesture} />
          <ResizeHandle dir="n"  layer={layer} scale={scale} aspectLocked={aspectLocked} onPatch={onPatch} onGesture={onGesture} />
          <ResizeHandle dir="ne" layer={layer} scale={scale} aspectLocked={aspectLocked} onPatch={onPatch} onGesture={onGesture} />
          <ResizeHandle dir="e"  layer={layer} scale={scale} aspectLocked={aspectLocked} onPatch={onPatch} onGesture={onGesture} />
          <ResizeHandle dir="se" layer={layer} scale={scale} aspectLocked={aspectLocked} onPatch={onPatch} onGesture={onGesture} />
          <ResizeHandle dir="s"  layer={layer} scale={scale} aspectLocked={aspectLocked} onPatch={onPatch} onGesture={onGesture} />
          <ResizeHandle dir="sw" layer={layer} scale={scale} aspectLocked={aspectLocked} onPatch={onPatch} onGesture={onGesture} />
          <ResizeHandle dir="w"  layer={layer} scale={scale} aspectLocked={aspectLocked} onPatch={onPatch} onGesture={onGesture} />
        </>
      )}
    </div>
  );
}

type ResizeDir = ResizeDirection;

const RESIZE_CURSOR: Record<ResizeDir, string> = {
  nw: 'nwse-resize', n: 'ns-resize', ne: 'nesw-resize', e: 'ew-resize', se: 'nwse-resize', s: 'ns-resize', sw: 'nesw-resize', w: 'ew-resize',
};

function ResizeHandle({
  onGesture,
  dir,
  layer,
  scale,
  aspectLocked = false,
  onPatch,
}: {
  dir: ResizeDir;
  layer: NoticeLayer;
  scale: number;
  aspectLocked?: boolean;
  onPatch: (patch: Partial<NoticeLayer>) => void;
  onGesture: (phase: 'start' | 'end') => void;
}) {
  const startRef = useRef({ x: 0, y: 0, width: 0, height: 0, widget: null as NoticeDataWidgetLayer | null });

  const bind = useDrag(
    ({ first, last, movement: [mx, my], event, shiftKey, altKey }) => {
      event.stopPropagation();
      if (first) onGesture('start');
      if (last) onGesture('end');
      if (first) {
        startRef.current = { x: layer.x, y: layer.y, width: layer.width, height: layer.height,
          widget: layer.kind === 'dataWidget' ? layer : null };
      }
      const vx = mx / Math.max(0.01, scale);
      const vy = my / Math.max(0.01, scale);
      const { x: sx, y: sy, width: sw, height: sh } = startRef.current;
      // Shift: 비율 유지(이미 고정이면 그대로) · Alt(⌥): 가운데 기준 — 드래그 중 누르고 떼도 다음 움직임부터 반영된다.
      const patch: Partial<NoticeLayer> & { fontSize?: number } = resizeBoundsFromHandle({ x: sx, y: sy, width: sw, height: sh }, dir, vx, vy, aspectLocked || shiftKey, altKey);

      if (startRef.current.widget && (patch.width !== undefined || patch.height !== undefined)) {
        patch.fontSize = noticeWidgetFontAtSize(startRef.current.widget, patch);
      }

      onPatch(patch);
    },
    { threshold: 3 },
  );

  // 화면 px ÷ 배율 — 대지 해상도·줌과 상관없이 보이는 10px, 잡히는 16px(바깥 투명 상자).
  const frame = resizeHandleFrame(dir, scale), px = 1 / Math.max(0.01, scale);
  const bindProps = bind() as Record<string, unknown>;
  return (
    <div
      {...bindProps}
      data-resize-handle={dir}
      onPointerDown={(e: React.PointerEvent) => {
        e.stopPropagation();
        (bindProps.onPointerDown as ((e: React.PointerEvent) => void) | undefined)?.(e);
      }}
      onClick={(e) => e.stopPropagation()}
      className="absolute pointer-events-auto touch-none flex items-center justify-center"
      style={{ cursor: RESIZE_CURSOR[dir], left: frame.left, top: frame.top, width: frame.size, height: frame.size }}
    >
      <span aria-hidden className="block shrink-0 bg-accent border-bg"
        style={{ width: HANDLE_VISIBLE_PX * px, height: HANDLE_VISIBLE_PX * px, borderWidth: 2 * px, borderRadius: 2 * px }} />
    </div>
  );
}


function TextLayerBody({
  layer,
  editing,
  onCommit,
  onEndEdit,
}: {
  layer: NoticeTextLayer;
  editing: boolean;
  onCommit: (text: string) => void;
  onEndEdit: () => void;
}) {
  const va = layer.verticalAlign ?? 'middle';
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // IME 조합 중 Enter가 커밋을 오발동하지 않도록 조합 상태를 추적한다.
  const composingRef = useRef(false);

  // 편집 진입 시 포커스 + 전체 선택 → 화면(그림 속 파란 선택 상태)대로 바로 덮어쓸 수 있게 한다.
  useEffect(() => {
    if (!editing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [editing]);

  const sharedTextStyle = {
    fontSize: layer.fontSize,
    fontWeight: layer.fontWeight,
    fontFamily: layer.fontFamily || undefined,
    color: layer.color,
    // Player canvas draws lines at fontSize × lineHeight (default 1.2); keep the editor identical.
    lineHeight: textLineHeight(layer),
    letterSpacing: layer.letterSpacing ? `${layer.letterSpacing}px` : undefined,
    textAlign: layer.align,
    whiteSpace: 'pre-wrap' as const,
    // Player 캔버스처럼 띄어쓰기 단위로 줄바꿈(긴 단어만 글자 단위) — 편집기와 실제 화면의 줄 수를 맞춘다.
    wordBreak: 'keep-all' as const,
    overflowWrap: 'anywhere' as const,
  };

  return (
    <div
      className="w-full h-full flex"
      style={{
        backgroundColor: layer.backgroundColor ?? 'transparent',
        borderRadius: cornerRadiusCss(layer.cornerRadius, layer.width, layer.height),
        padding: `${layer.paddingY}px ${layer.paddingX}px`,
        justifyContent:
          layer.align === 'center'
            ? 'center'
            : layer.align === 'right'
              ? 'flex-end'
              : 'flex-start',
        alignItems: va === 'top' ? 'flex-start' : va === 'bottom' ? 'flex-end' : 'center',
      }}
    >
      {editing ? (
        <textarea
          ref={textareaRef}
          value={layer.text}
          onChange={(e) => onCommit(e.target.value)}
          onCompositionStart={() => (composingRef.current = true)}
          onCompositionEnd={() => (composingRef.current = false)}
          onBlur={onEndEdit}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') {
              e.preventDefault();
              onEndEdit();
            } else if (e.key === 'Enter' && !e.shiftKey && !composingRef.current) {
              // Enter = 편집 종료, Shift+Enter = 줄바꿈.
              e.preventDefault();
              onEndEdit();
            }
          }}
          className="w-full resize-none border-0 bg-transparent p-0 outline-none"
          style={{ ...sharedTextStyle, height: '100%' }}
        />
      ) : (
        <span style={sharedTextStyle}>
          {/* A filled box (panel / divider) is visible on its own; the hint is only for invisible empty text. */}
          {layer.text || (!layer.backgroundColor && <span className="opacity-40">(empty text)</span>)}
        </span>
      )}
    </div>
  );
}

// 플레이어·썸네일과 같은 경로(noticeShapePath)를 SVG로 그린다. viewBox = 레이어 px 상자.
function ShapeLayerBody({ layer }: { layer: NoticeShapeLayer }) {
  return (
    <svg className="absolute inset-0 h-full w-full overflow-visible" viewBox={`0 0 ${layer.width} ${layer.height}`} preserveAspectRatio="none" aria-hidden="true">
      <path
        d={noticeShapePath(layer)}
        fill={layer.shape !== 'line' && layer.fill ? layer.fill : 'none'}
        stroke={layer.stroke && layer.strokeWidth > 0 ? layer.stroke : 'none'}
        strokeWidth={layer.strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ImageLayerBody({
  layer,
  media,
}: {
  layer: NoticeImageLayer;
  media: MediaItem | undefined;
}) {
  if (!media) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-panel-2 text-text-dimmer text-[14px]">
        (missing media)
      </div>
    );
  }
  return (
    <div className="relative w-full h-full overflow-hidden" style={{ borderRadius: cornerRadiusCss(layer.cornerRadius, layer.width, layer.height) }}>
      <img
        src={api.mediaFileUrl(media.id)}
        alt=""
        className="absolute left-1/2 top-1/2 w-full h-full"
        style={{
          objectFit: layer.fit === 'stretch' ? 'fill' : layer.fit,
          transform: mediaTransform(layer),
          transformOrigin: 'center center',
        }}
        draggable={false}
      />
    </div>
  );
}

function VideoLayerBody({
  layer,
  media,
}: {
  layer: NoticeVideoLayer;
  media: MediaItem | undefined;
}) {
  if (!media) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-panel-2 text-text-dimmer text-[14px]">
        (missing video)
      </div>
    );
  }
  return (
    <div className="relative w-full h-full overflow-hidden" style={{ borderRadius: cornerRadiusCss(layer.cornerRadius, layer.width, layer.height) }}>
      <video
        src={api.mediaFileUrl(media.id)}
        poster={media.thumbnailRelPath ? api.mediaThumbnailUrl(media.id) : undefined}
        autoPlay
        loop={layer.loop}
        muted={layer.muted}
        playsInline
        className="absolute left-1/2 top-1/2 w-full h-full"
        style={{
          objectFit: layer.fit === 'stretch' ? 'fill' : layer.fit,
          transform: mediaTransform(layer),
          transformOrigin: 'center center',
        }}
      />
    </div>
  );
}

function mediaTransform(layer: NoticeImageLayer | NoticeVideoLayer): string {
  const sx = layer.mediaOffsetX ?? 0;
  const sy = layer.mediaOffsetY ?? 0;
  const scale = layer.mediaScale ?? 1;
  return `translate(-50%, -50%) translate(${sx}%, ${sy}%) scale(${scale})`;
}

function isWebmMedia(media: MediaItem): boolean {
  return media.mediaType === 'video'
    && (media.mimeType === 'video/webm' || /\.webm$/i.test(media.originalName));
}

function LottieLayerBody({
  layer,
  media,
}: {
  layer: NoticeLottieLayer;
  media: MediaItem | undefined;
}) {
  return (
    <div className="w-full h-full flex items-center justify-center bg-purple/15 border border-purple/60 text-purple font-mono text-[34px] text-center p-6">
      <div>
        <div>LOTTIE</div>
        <div className="mt-2 text-[19px] text-text-dim break-all">
          {media?.originalName ?? layer.mediaId}
        </div>
      </div>
    </div>
  );
}

function RoomCardLayerBody({ layer }: { layer: NoticeRoomCardLayer }) {
  const rooms = useLibraryStore((s) => s.calendarRooms);
  const events = useLibraryStore((s) => s.calendarEvents);
  const loadCalendarRooms = useLibraryStore((s) => s.loadCalendarRooms);
  const loadCalendarEvents = useLibraryStore((s) => s.loadCalendarEvents);

  useEffect(() => {
    void loadCalendarRooms();
    const now = Date.now();
    void loadCalendarEvents({
      from: new Date(now - 12 * 60 * 60 * 1000).toISOString(),
      to: new Date(now + 36 * 60 * 60 * 1000).toISOString(),
    });
  }, [loadCalendarEvents, loadCalendarRooms]);

  const room = rooms.find((r) => r.id === layer.calendarIntegrationId);
  const roomEvents = events.filter(
    (event) => event.integrationId === layer.calendarIntegrationId,
  );

  return (
    <RoomCardScaledBox
      layer={layer}
      room={room}
      events={roomEvents}
      nowMs={Date.now()}
      canvasWidth={layer.width}
      canvasHeight={layer.height}
      style={{ width: '100%', height: '100%' }}
    />
  );
}

function RoomTemplateLayerBody({ layer }: { layer: NoticeRoomTemplateLayer }) {
  const rooms = useLibraryStore((s) => s.calendarRooms);
  const events = useLibraryStore((s) => s.calendarEvents);
  const loadCalendarRooms = useLibraryStore((s) => s.loadCalendarRooms);
  const loadCalendarEvents = useLibraryStore((s) => s.loadCalendarEvents);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    void loadCalendarRooms();
    const now = Date.now();
    void loadCalendarEvents({
      from: new Date(now - 12 * 60 * 60 * 1000).toISOString(),
      to: new Date(now + 36 * 60 * 60 * 1000).toISOString(),
    });
  }, [loadCalendarEvents, loadCalendarRooms]);

  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const room = rooms.find((r) => r.id === layer.calendarIntegrationId);
  const roomEvents = layer.dataMode === 'dummy'
    ? dummyRoomTemplateEvents(nowMs, layer.calendarIntegrationId ?? 'dummy-room')
    : events.filter((event) => event.integrationId === layer.calendarIntegrationId);

  return (
    <RoomTemplate
      layer={layer}
      room={room}
      events={roomEvents}
      nowMs={nowMs}
      canvasWidth={layer.width}
      canvasHeight={layer.height}
    />
  );
}

// Admin-side preview of the BRICK BRIEF card-only template. The Player renders
// the same transparent layer: no page background, title art, or right-side art.
const DUMMY_BRICK_BRIEF_SLOTS: NoticeBrickBriefLayer['slots'] = {
  header: '업무시간 변경 안내',
  title: '주간 운영 시간 조정 안내',
  date: '2026년 5월 18일 (월)',
  time: '14:00~15:00',
  location: '본관 7층 라운지',
  content:
    '시설 점검으로 인해 일부 업무 시간이 조정됩니다.\n이용 전 공지사항을 확인해 주세요.',
};

const EMPTY_LIVE_BRICK_BRIEF_SLOTS: NoticeBrickBriefLayer['slots'] = {
  header: '조건에 맞는 공지 없음',
  title: '실제 데이터 없음',
  date: '',
  time: '',
  location: '',
  content: '오른쪽 데이터 필터를 조정하거나 Chat 연동 폴링 결과를 확인해 주세요.',
};

function useBrickBriefPreviewSlots(
  layer: NoticeBrickBriefLayer,
): NoticeBrickBriefLayer['slots'] {
  const source = layer.source ?? { kind: 'static' as const };
  const [pool, setPool] = useState<ChatRecentMessage[]>([]);

  useEffect(() => {
    if (source.kind !== 'chatRotation') {
      setPool([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const u = new URL(`${API_BASE}/api/integrations/chat/rotation-pool`);
        u.searchParams.set('maxAgeMonths', String(source.maxAgeMonths));
        u.searchParams.set('statuses', source.includeStatuses.join(','));
        if (source.chatIntegrationIds && source.chatIntegrationIds.length > 0) {
          u.searchParams.set('chatIntegrationIds', source.chatIntegrationIds.join(','));
        }
        const res = await fetch(u.toString());
        if (!res.ok) return;
        const data = (await res.json()) as ChatRecentMessage[];
        if (!cancelled) setPool(data);
      } catch {
        if (!cancelled) setPool([]);
      }
    };
    void load();
    const t = window.setInterval(() => void load(), 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [
    source.kind,
    source.kind === 'chatRotation' ? source.maxAgeMonths : 0,
    source.kind === 'chatRotation' ? source.includeStatuses.join(',') : '',
    source.kind === 'chatRotation' ? (source.chatIntegrationIds ?? []).join(',') : '',
  ]);

  if (source.kind === 'dummy') return DUMMY_BRICK_BRIEF_SLOTS;
  if (source.kind !== 'chatRotation') return layer.slots;
  const parsed = pool.find((m) => m.parsed)?.parsed;
  return parsed ? parsedToBrickBriefSlots(parsed, layer.slots) : EMPTY_LIVE_BRICK_BRIEF_SLOTS;
}

function parsedToBrickBriefSlots(
  parsed: ParsedNotice,
  fallback: NoticeBrickBriefLayer['slots'],
): NoticeBrickBriefLayer['slots'] {
  return {
    header: parsed.header || fallback.header,
    title: parsed.subtitle || fallback.title,
    date: parsed.metaData.date ?? fallback.date,
    time: parsed.metaData.time ?? fallback.time,
    location: parsed.metaData.location ?? fallback.location,
    content: parsed.content || fallback.content,
  };
}

function BrickBriefLayerBody({ layer }: { layer: NoticeBrickBriefLayer }) {
  const slots = useBrickBriefPreviewSlots(layer);
  return <BrickBriefDataLayerBody layer={layer} slots={slots} />;
}

// Admin-side preview of the BRICK BRIEF card-only skin. Mirrors
// brickBriefCompositor.ts paintData in the 1920×1080 virtual space so the
// editor preview matches the Player output.
function BrickBriefDataLayerBody({
  layer,
  slots,
}: {
  layer: NoticeBrickBriefLayer;
  slots: NoticeBrickBriefLayer['slots'];
}) {
  const card = { x: 68, top: 199, w: 827, h: 814, headerH: 101 };
  const headerX = 121;
  const contentX = 124;
  const valueW = 672;
  const bodyW = 714;
  // Mirrors brickBriefCompositor.ts: 제목 always renders; 일시·시간·장소 are
  // skipped when their slot is empty, and visible rows + the body paragraph
  // reflow upward to close the gap.
  const ROWS_TOP = 341;
  const ROW_STRIDE = 124;
  const VALUE_OFFSET = 45;
  const BODY_GAP = 1;
  const visibleRows = [
    { label: '제목', value: slots.title, optional: false },
    { label: '일시', value: slots.date, optional: true },
    { label: '시간', value: slots.time, optional: true },
    { label: '장소', value: slots.location, optional: true },
  ]
    .filter((r) => !r.optional || r.value.trim() !== '')
    .map((r, i) => {
      const chipY = ROWS_TOP + i * ROW_STRIDE;
      return { label: r.label, value: r.value, chipY, valueY: chipY + VALUE_OFFSET };
    });
  const bodyTop = ROWS_TOP + visibleRows.length * ROW_STRIDE + BODY_GAP;
  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        background: 'transparent',
        fontFamily: 'SUIT, sans-serif',
      }}
    >
      {/* Card */}
      <div
        className="absolute"
        style={{
          left: card.x,
          top: card.top,
          width: card.w,
          height: card.h,
          borderRadius: 20,
          background: '#ffffff',
          border: '2px solid #1350a3',
          boxShadow: '7px 7px 0 #1350a3',
        }}
      >
        {/* Lime header band */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: card.headerH,
            background: '#dbf790',
            borderTopLeftRadius: 18,
            borderTopRightRadius: 18,
          }}
        />
        {/* Header title */}
        <div
          className="absolute truncate flex items-center"
          style={{
            left: headerX - card.x,
            top: 0,
            width: 654,
            height: card.headerH,
            fontWeight: 700,
            fontSize: 44,
            color: '#00182f',
          }}
        >
          {slots.header || '(헤더)'}
        </div>
        {/* X close glyph — deep-blue accent, thin weight (matches Figma). */}
        <div
          className="absolute flex items-center justify-center"
          style={{
            right: 36,
            top: 0,
            width: 34,
            height: card.headerH,
            fontSize: 52,
            fontWeight: 400,
            color: '#1350a3',
          }}
        >
          ✕
        </div>
        {/* Label chips + value rows */}
        {visibleRows.map((r) => (
          <div key={r.label}>
            <span
              className="absolute inline-flex items-center justify-center"
              style={{
                left: contentX - card.x,
                top: r.chipY - card.top,
                width: 59,
                height: 35,
                background: '#dbf790',
                border: '2px solid #1350a3',
                borderRadius: 10,
                fontSize: 20,
                fontWeight: 500,
                color: '#00182f',
              }}
            >
              {r.label}
            </span>
            <div
              className="absolute truncate"
              style={{
                left: contentX - card.x,
                top: r.valueY - card.top,
                width: valueW,
                fontSize: 32,
                fontWeight: 500,
                color: '#00182f',
              }}
            >
              {r.value || '—'}
            </div>
          </div>
        ))}
        {/* Body paragraph */}
        <div
          className="absolute"
          style={{
            left: contentX - card.x,
            top: bodyTop - card.top,
            width: bodyW,
            fontSize: 24,
            fontWeight: 600,
            color: '#a342f3',
            lineHeight: '30px',
            whiteSpace: 'pre-wrap',
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {slots.content || '(본문)'}
        </div>
      </div>
    </div>
  );
}

function RoomStatusLayerBody({ layer }: { layer: NoticeRoomStatusLayer }) {
  // Live preview inside the editor — uses the same RoomBoard the Player
  // composites, fed from the calendar slice the Rooms tab already loads.
  const allRooms = useLibraryStore((s) => s.calendarRooms);
  const events = useLibraryStore((s) => s.calendarEvents);
  const loadCalendarRooms = useLibraryStore((s) => s.loadCalendarRooms);
  const loadCalendarEvents = useLibraryStore((s) => s.loadCalendarEvents);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    void loadCalendarRooms();
    const win = computeWindow(
      Date.now(),
      layer.windowStartHour,
      layer.windowEndHour,
    );
    void loadCalendarEvents({
      from: new Date(win.startMs).toISOString(),
      to: new Date(win.endMs).toISOString(),
    });
  }, [loadCalendarRooms, loadCalendarEvents, layer.windowStartHour, layer.windowEndHour]);

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const rooms = useMemo(() => {
    const enabled = allRooms.filter((r) => r.enabled);
    if (layer.roomIntegrationIds === null) return enabled;
    const byId = new Map(enabled.map((r) => [r.id, r] as const));
    return layer.roomIntegrationIds
      .map((id) => byId.get(id))
      .filter((r): r is NonNullable<typeof r> => !!r);
  }, [allRooms, layer.roomIntegrationIds]);

  const eventsByRoomId = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>();
    for (const ev of events) {
      const arr = m.get(ev.integrationId);
      if (arr) arr.push(ev);
      else m.set(ev.integrationId, [ev]);
    }
    return m;
  }, [events]);

  // Inside the editor, the layer is placed inside the virtual 1920×1080 canvas
  // at (layer.x, layer.y, layer.width, layer.height). Scale-fit using the
  // layer's intrinsic dimensions so fonts inside match the Player Canvas2D
  // output regardless of the editor preview's pixel size.
  return (
    <div className="w-full h-full">
      <RoomBoardScaledBox
        rooms={rooms}
        eventsByRoomId={eventsByRoomId}
        nowMs={nowMs}
        windowStartHour={layer.windowStartHour}
        windowEndHour={layer.windowEndHour}
        orientation={layer.orientation ?? 'vertical'}
        canvasWidth={layer.width}
        canvasHeight={layer.height}
        style={{ height: '100%' }}
      />
    </div>
  );
}

function BrickBriefSlotsEditor({
  layer,
  onSlotsPatch,
  onSourceChange,
}: {
  layer: NoticeBrickBriefLayer;
  onSlotsPatch: (patch: Partial<NoticeBrickBriefLayer['slots']>) => void;
  onSourceChange: (source: NoticeBrickBriefSource | undefined) => void;
}) {
  const source: NoticeBrickBriefSource =
    layer.source ?? { kind: 'static' };
  const isRotation = source.kind === 'chatRotation';
  // Per-variant character caps. The "data" skin (Figma "3 Data") has a fixed
  // card layout, so its three free-text slots are length-limited.
  const maxLen: Partial<
    Record<keyof NoticeBrickBriefLayer['slots'], number>
  > = { header: 17, title: 24, content: 102 };
  const fields: Array<{
    key: keyof NoticeBrickBriefLayer['slots'];
    label: string;
    multiline?: boolean;
  }> = [
    { key: 'header', label: '헤더' },
    { key: 'title', label: '제목' },
    { key: 'date', label: '일시' },
    { key: 'time', label: '시간' },
    { key: 'location', label: '장소' },
    { key: 'content', label: '본문', multiline: true },
  ];
  return (
    <div className="flex flex-col gap-2 pt-2 border-t border-line">
      <label className="flex flex-col gap-1">
        <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
          데이터
        </span>
        <select
          value={source.kind}
          onChange={(e) => {
            const next = e.target.value as NoticeBrickBriefSource['kind'];
            if (next === 'static') {
              onSourceChange(undefined);
            } else if (next === 'dummy') {
              onSourceChange({ kind: 'dummy' });
            } else {
              onSourceChange({
                kind: 'chatRotation',
                intervalMs: 10_000,
                maxAgeMonths: 5,
                includeStatuses: ['upcoming', 'ongoing', 'expired', 'unknown'],
                chatIntegrationIds: null,
              });
            }
          }}
          className="px-2 py-1 bg-panel-2 border border-line rounded text-[14px]"
        >
          <option value="static">수동 입력</option>
          <option value="dummy">더미 데이터</option>
          <option value="chatRotation">실제 데이터 (Chat 회전)</option>
        </select>
      </label>

      {isRotation && source.kind === 'chatRotation' && (
        <ChatRotationSourceEditor
          source={source}
          onChange={(next) => onSourceChange(next)}
        />
      )}

      {source.kind !== 'static' && (
        <div className="font-mono text-[12px] text-text-dimmer leading-[1.4] rounded border border-line bg-panel-2 p-2">
          {source.kind === 'dummy'
            ? '캔버스에는 더미 데이터가 표시됩니다. 저장되는 수동 슬롯 값은 변경하지 않습니다.'
            : '캔버스에는 현재 후보 풀의 첫 번째 실제 데이터가 표시됩니다. 후보가 없으면 “조건에 맞는 공지 없음” 상태가 표시됩니다.'}
        </div>
      )}

      {source.kind === 'static' && (
        <>
          <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim mt-1">
            BRICK BRIEF Slots
          </span>
          {fields.map(({ key, label, multiline }) => {
            const cap = maxLen[key];
            return (
              <label key={key} className="flex flex-col gap-1">
                <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
                  {label}
                  {cap != null && (
                    <span className="ml-1 text-text-dimmer">
                      · {layer.slots[key].length}/{cap}
                    </span>
                  )}
                </span>
                {multiline ? (
                  <textarea
                    value={layer.slots[key]}
                    onChange={(e) => onSlotsPatch({ [key]: e.target.value })}
                    rows={3}
                    {...(cap != null ? { maxLength: cap } : {})}
                    className="px-2 py-1 bg-panel-2 border border-line rounded text-[14px]"
                  />
                ) : (
                  <input
                    value={layer.slots[key]}
                    onChange={(e) => onSlotsPatch({ [key]: e.target.value })}
                    {...(cap != null ? { maxLength: cap } : {})}
                    className="px-2 py-1 bg-panel-2 border border-line rounded text-[14px]"
                  />
                )}
              </label>
            );
          })}
        </>
      )}
    </div>
  );
}

/** Korean labels for the four NoticeStatus values. Matches the badge labels
 *  used in the Integrations "폴링 결과" modal so operators see one
 *  vocabulary across the admin surface. */
const STATUS_OPTIONS: ReadonlyArray<{ value: NoticeStatus; label: string }> = [
  { value: 'upcoming', label: '예정' },
  { value: 'ongoing', label: '진행중' },
  { value: 'expired', label: '지난 공지' },
  { value: 'unknown', label: '시점 없음' },
];

function statusLabel(s: NoticeStatus): string {
  return STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s;
}

function ChatRotationSourceEditor({
  source,
  onChange,
}: {
  source: Extract<NoticeBrickBriefSource, { kind: 'chatRotation' }>;
  onChange: (next: NoticeBrickBriefSource) => void;
}) {
  const [pool, setPool] = useState<ChatRecentMessage[] | null>(null);
  const [poolError, setPoolError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const u = new URL(`${API_BASE}/api/integrations/chat/rotation-pool`);
        u.searchParams.set('maxAgeMonths', String(source.maxAgeMonths));
        u.searchParams.set('statuses', source.includeStatuses.join(','));
        if (source.chatIntegrationIds && source.chatIntegrationIds.length > 0) {
          u.searchParams.set(
            'chatIntegrationIds',
            source.chatIntegrationIds.join(','),
          );
        }
        const res = await fetch(u.toString());
        if (!res.ok) throw new Error(`${res.status}`);
        const data = (await res.json()) as ChatRecentMessage[];
        if (!cancelled) {
          setPool(data);
          setPoolError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setPoolError(err instanceof Error ? err.message : String(err));
        }
      }
    };
    void load();
    const t = window.setInterval(() => void load(), 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [source.maxAgeMonths, source.includeStatuses.join(','), (source.chatIntegrationIds ?? []).join(',')]);

  const toggleStatus = (s: NoticeStatus) => {
    const has = source.includeStatuses.includes(s);
    const next = has
      ? source.includeStatuses.filter((x) => x !== s)
      : [...source.includeStatuses, s];
    if (next.length === 0) return; // never empty
    onChange({ ...source, includeStatuses: next });
  };

  // Operator drops a message from the rotation pool. The pool query only
  // returns excluded=0 rows, so an optimistic local removal matches what the
  // next refetch will show. Reverts on error. Re-including is done from the
  // Integrations "폴링 결과" modal.
  const excludeFromPool = async (m: ChatRecentMessage) => {
    setPool((prev) => prev?.filter((x) => x.messageId !== m.messageId) ?? prev);
    try {
      const res = await fetch(
        `${API_BASE}/api/integrations/chat/${encodeURIComponent(
          m.integrationId,
        )}/message`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messageId: m.messageId, excluded: true }),
        },
      );
      if (!res.ok) throw new Error(`${res.status}`);
    } catch (err) {
      setPool((prev) => (prev ? [...prev, m] : [m]));
      // 실패 알림은 앱 전역 토스트로 통일(2026-09-16 검수 S19).
      toast(`제외 실패 — ${describeApiError(err)}`, 'error');
    }
  };

  return (
    <div className="flex flex-col gap-2 p-2 border border-line rounded bg-panel-2">
      <Field
        label="최대 기간 (개월)"
        type="number"
        min={1}
        max={60}
        value={String(source.maxAgeMonths)}
        onChange={(e) => {
          const n = Math.max(1, Math.min(60, Number(e.target.value) || 5));
          onChange({ ...source, maxAgeMonths: n });
        }}
      />
      <div className="font-mono text-[12px] text-text-dimmer leading-[1.4]">
        회전 방식: 노출 횟수 기반. 공지가 한 번 재생될 때마다 다음 메시지로 자동 이동하며, 풀의 끝에 도달하면 처음으로 돌아갑니다.
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
          포함 status
        </span>
        <div className="grid grid-cols-2 gap-1">
          {STATUS_OPTIONS.map(({ value, label }) => (
            <label
              key={value}
              className="flex items-center gap-1.5 text-[13px] px-1.5 py-1 border border-line rounded bg-panel cursor-pointer"
            >
              <input
                type="checkbox"
                checked={source.includeStatuses.includes(value)}
                onChange={() => toggleStatus(value)}
                className="accent-accent"
              />
              {label}
            </label>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
          현재 후보 풀
          {pool ? ` (${pool.length}개)` : ''}
        </span>
        {poolError && (
          <span className="font-mono text-[12px] text-red">⚠ {poolError}</span>
        )}
        {pool && pool.length === 0 && (
          <span className="font-mono text-[12px] text-text-dimmer">
            조건에 맞는 공지 없음. 플레이스홀더 슬롯으로 표시됩니다.
          </span>
        )}
        {pool && pool.length > 0 && (
          <div className="max-h-40 overflow-auto flex flex-col gap-1">
            {pool.slice(0, 12).map((m, idx) => (
              <div
                key={m.messageId}
                className="flex items-start gap-1.5 text-[13px] px-1.5 py-1 border border-line rounded bg-panel"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-ui text-text truncate">
                    {idx + 1}. {m.parsed?.header || '(no header)'}
                  </div>
                  <div className="font-mono text-[11px] text-text-dim truncate">
                    {statusLabel(m.parsed?.status ?? 'unknown')} ·{' '}
                    {m.messageCreateTime
                      ? new Date(m.messageCreateTime).toLocaleDateString()
                      : '시점 없음'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void excludeFromPool(m)}
                  title="이 공지를 회전 풀에서 제외"
                  className="shrink-0 px-1.5 py-0.5 text-[12px] border border-line rounded text-text-dim hover:text-red hover:border-red"
                >
                  ✕ 제외
                </button>
              </div>
            ))}
            {pool.length > 12 && (
              <div className="font-mono text-[12px] text-text-dimmer">
                ... 외 {pool.length - 12}개
              </div>
            )}
          </div>
        )}
        {pool && pool.length > 0 && (
          <span className="font-mono text-[11px] text-text-dimmer leading-[1.4]">
            제외한 공지는 Integrations · 폴링 결과에서 다시 포함할 수 있습니다.
          </span>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Room-status layer editor — pick included rooms + adjust the 12 h window.
// Mirrors the Rooms tab controls so the editor and the tab stay consistent.
// -----------------------------------------------------------------------------
function RoomStatusEditor({
  layer,
  onPatch,
}: {
  layer: NoticeRoomStatusLayer;
  onPatch: (patch: Partial<NoticeRoomStatusLayer>) => void;
}) {
  const rooms = useLibraryStore((s) => s.calendarRooms);
  const loadCalendarRooms = useLibraryStore((s) => s.loadCalendarRooms);
  useEffect(() => {
    void loadCalendarRooms();
  }, [loadCalendarRooms]);

  const enabledRooms = useMemo(
    () => rooms.filter((r) => r.enabled),
    [rooms],
  );
  const isAuto = layer.roomIntegrationIds === null;
  const list = layer.roomIntegrationIds ?? enabledRooms.map((r) => r.id);

  const toggle = (id: string) => {
    const base = list;
    const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    onPatch({ roomIntegrationIds: next });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
          포함 회의실
        </span>
        <button
          type="button"
          onClick={() =>
            onPatch({ roomIntegrationIds: isAuto ? enabledRooms.map((r) => r.id) : null })
          }
          className="text-[12px] font-mono text-text-dim hover:text-accent"
        >
          {isAuto ? '수동 선택' : '자동(전체)'}
        </button>
      </div>
      <div className="flex flex-col gap-1 max-h-40 overflow-auto">
        {enabledRooms.map((r) => {
          const checked = list.includes(r.id);
          return (
            <label
              key={r.id}
              className="flex items-center gap-2 px-1.5 py-1 text-[13px] border border-line rounded-sm bg-panel-2"
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={isAuto}
                onChange={() => toggle(r.id)}
                className="accent-accent"
              />
              <span className={cn('flex-1 truncate', isAuto && 'text-text-dim')}>
                {r.label}
              </span>
            </label>
          );
        })}
        {enabledRooms.length === 0 && (
          <div className="px-1.5 py-2 text-[12px] font-mono text-text-dimmer">
            등록된 회의실이 없습니다.
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field
          label="Window start (h)"
          value={String(layer.windowStartHour)}
          onChange={(e) => {
            const n = Math.max(0, Math.min(23, Number(e.target.value) || 0));
            if (n < layer.windowEndHour) onPatch({ windowStartHour: n });
          }}
        />
        <Field
          label="Window end (h)"
          value={String(layer.windowEndHour)}
          onChange={(e) => {
            const n = Math.max(0, Math.min(23, Number(e.target.value) || 0));
            if (n > layer.windowStartHour) onPatch({ windowEndHour: n });
          }}
        />
      </div>
      <label className="flex flex-col gap-1">
        <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
          레이아웃 방향
        </span>
        <select
          value={layer.orientation ?? 'vertical'}
          onChange={(e) =>
            onPatch({ orientation: e.target.value as 'horizontal' | 'vertical' })
          }
          className="px-1.5 py-1 bg-panel-2 border border-line rounded text-[14px]"
        >
          <option value="vertical">세로 시간축 (룸=컬럼)</option>
          <option value="horizontal">가로 시간축 (룸=로우)</option>
        </select>
      </label>
    </div>
  );
}

function RoomCardEditor({
  layer,
  onPatch,
}: {
  layer: NoticeRoomCardLayer;
  onPatch: (patch: Partial<NoticeRoomCardLayer>) => void;
}) {
  const rooms = useLibraryStore((s) => s.calendarRooms);
  const loadCalendarRooms = useLibraryStore((s) => s.loadCalendarRooms);
  useEffect(() => {
    void loadCalendarRooms();
  }, [loadCalendarRooms]);

  const enabledRooms = useMemo(
    () => rooms.filter((r) => r.enabled),
    [rooms],
  );

  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-1">
        <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
          Theme
        </span>
        <select
          value={layer.theme}
          onChange={(e) =>
            onPatch({ theme: e.target.value as NoticeRoomCardLayer['theme'] })
          }
          className="px-1.5 py-1 bg-panel-2 border border-line rounded text-[14px]"
        >
          {ROOM_CARD_THEMES.map((theme) => (
            <option key={theme} value={theme}>
              {theme}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
          회의실
        </span>
        <select
          value={layer.calendarIntegrationId}
          onChange={(e) => onPatch({ calendarIntegrationId: e.target.value })}
          className="px-1.5 py-1 bg-panel-2 border border-line rounded text-[14px]"
        >
          <option value="">-- 회의실을 선택하세요 --</option>
          {enabledRooms.map((room) => (
            <option key={room.id} value={room.id}>
              {room.label}
            </option>
          ))}
        </select>
        {!layer.calendarIntegrationId.trim() ? (
          <span className="text-[12px] text-red">
            회의실 선택 전에는 Scene을 저장할 수 없습니다.
          </span>
        ) : null}
      </label>
      <Field
        label="Title (blank = auto)"
        value={layer.title ?? ''}
        onChange={(e) => onPatch({ title: e.target.value || null })}
      />
      <label className="flex items-center gap-2 text-[13px]">
        <input
          type="checkbox"
          checked={layer.showLogo}
          onChange={(e) => onPatch({ showLogo: e.target.checked })}
          className="accent-accent"
        />
        <span>Show logo</span>
      </label>
    </div>
  );
}

function RoomTemplateEditor({
  layer,
  onPatch,
}: {
  layer: NoticeRoomTemplateLayer;
  onPatch: (patch: Partial<NoticeRoomTemplateLayer>) => void;
}) {
  const rooms = useLibraryStore((s) => s.calendarRooms);
  const loadCalendarRooms = useLibraryStore((s) => s.loadCalendarRooms);
  useEffect(() => {
    void loadCalendarRooms();
  }, [loadCalendarRooms]);

  const enabledRooms = useMemo(
    () => rooms.filter((r) => r.enabled),
    [rooms],
  );

  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-1">
        <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
          디자인 템플릿
        </span>
        <select
          value={layer.theme}
          onChange={(e) =>
            onPatch({ theme: e.target.value as NoticeRoomTemplateLayer['theme'] })
          }
          className="px-1.5 py-1 bg-panel-2 border border-line rounded text-[14px]"
        >
          {ROOM_CARD_THEMES.map((theme) => (
            <option key={theme} value={theme}>
              {theme}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
          데이터
        </span>
        <select
          value={layer.dataMode}
          onChange={(e) =>
            onPatch({ dataMode: e.target.value as NoticeRoomTemplateLayer['dataMode'] })
          }
          className="px-1.5 py-1 bg-panel-2 border border-line rounded text-[14px]"
        >
          <option value="dummy">더미 데이터</option>
          <option value="live">실제 캘린더</option>
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
          회의실
        </span>
        <select
          value={layer.calendarIntegrationId ?? ''}
          onChange={(e) => onPatch({ calendarIntegrationId: e.target.value || null })}
          className="px-1.5 py-1 bg-panel-2 border border-line rounded text-[14px]"
        >
          <option value="">-- 회의실을 선택하세요 --</option>
          {enabledRooms.map((room) => (
            <option key={room.id} value={room.id}>
              {room.label}
            </option>
          ))}
        </select>
        {layer.dataMode === 'live' && !layer.calendarIntegrationId ? (
          <span className="text-[12px] text-red">
            실제 캘린더 모드는 회의실 선택이 필요합니다.
          </span>
        ) : null}
      </label>
      <div className="font-mono text-[12px] text-text-dimmer leading-[1.4]">
        배경과 장식은 렌더하지 않고, Figma 기준 타임 스케줄 카드만 투명 레이어로 표시합니다.
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Canvas (대지) properties — shown when no layer is selected.
// -----------------------------------------------------------------------------
function CanvasProperties({
  canvasW,
  canvasH,
  devices,
  pick,
  onPick,
  fill,
  fillOpacity,
  fitMode,
  onFitMode,
  onSize,
  onFill,
  exporting,
  onExport,
}: {
  canvasW: number;
  canvasH: number;
  /** 해상도가 확인된 등록 기기(SCDS-115). */
  devices: Array<{ id: string; name: string; w: number; h: number }>;
  pick: string | null;
  onPick(value: string): void;
  fill: string | null;
  fillOpacity: number;
  fitMode: NonNullable<Notice['fitMode']>;
  onFitMode(value: NonNullable<Notice['fitMode']>): void;
  onSize: (w: number, h: number) => void;
  onFill: (fill: string | null, opacity: number) => void;
  exporting: boolean;
  onExport(): void;
}) {
  // 기기는 'device:<id>', 표준 해상도는 그 라벨이 값이다. 고른 값이 지금 대지 크기와 맞을 때만 그대로 보이고,
  // 아니면 대지 크기로 추정한다 — 같은 해상도의 기기가 여럿이어도 고른 기기가 남고, '직접 입력'으로도 빠져나올 수 있다.
  const sizeOf = (value: string) => value.startsWith('device:') ? devices.find((d) => `device:${d.id}` === value) : DEVICE_PRESETS.find((p) => p.label === value);
  const fits = (value: string) => { const size = sizeOf(value); return !!size && size.w === canvasW && size.h === canvasH; };
  const matchedDevice = devices.find((d) => d.w === canvasW && d.h === canvasH);
  const presetValue = pick === '' || (pick && fits(pick)) ? pick
    : DEVICE_PRESETS.find((p) => p.w === canvasW && p.h === canvasH)?.label ?? (matchedDevice ? `device:${matchedDevice.id}` : '');
  // 기기를 고르면 W·H 를 그 기기 해상도로 고정한다(안내 문구 '디바이스 선택 시 레이아웃 값이 고정됩니다', 플레이리스트 추가 창과 같은 규칙).
  const pickedDevice = presetValue.startsWith('device:') ? devices.find((d) => `device:${d.id}` === presetValue) ?? null : null;
  return (
    <div className="flex flex-col gap-4">
      <InspectorTitle>대지</InspectorTitle>
      <PropSection
        title={
          <span className="flex items-center gap-1">
            디바이스 선택
            <InfoTooltip text="디바이스 선택 시 레이아웃 값이 고정됩니다." />
          </span>
        }
      >
        <select
          aria-label="대지 해상도"
          value={presetValue}
          onChange={(e) => {
            onPick(e.target.value);
            const size = sizeOf(e.target.value);
            if (size) onSize(size.w, size.h);
          }}
          className="w-full px-2.5 py-2 bg-panel-2 border border-line rounded-md text-[14px]"
        >
          <option value="">직접 입력 · {canvasW}×{canvasH}</option>
          <optgroup label="등록된 기기">
            {devices.length ? devices.map((d) => <option key={d.id} value={`device:${d.id}`}>{d.name} — {d.w}×{d.h}</option>)
              : <option value="device:none" disabled>해상도가 확인된 기기가 없습니다</option>}
          </optgroup>
          <optgroup label="표준 해상도">
            {DEVICE_PRESETS.map((p) => (
              <option key={p.label} value={p.label}>
                {p.label}
              </option>
            ))}
          </optgroup>
        </select>
        {pickedDevice && <p className="mt-1.5 text-[12px] text-text-dim">‘{pickedDevice.name}’ 해상도로 고정했습니다. 직접 정하려면 ‘직접 입력’을 고르세요.</p>}
      </PropSection>
      <PropSection title="송출 맞춤">
        <FitModeRadio label="슬라이드 송출 맞춤" size="md" value={fitMode} onChange={value => onFitMode(value as NonNullable<Notice['fitMode']>)} />
        <p className="mt-2 text-[12px] text-text-dim">{fitMode === 'contain' ? '내용을 자르거나 왜곡하지 않고 표시합니다. 화면 비율이 다르면 여백이 생깁니다.' : fitMode === 'cover' ? '비율을 유지하면서 화면을 채웁니다. 화면을 벗어나는 가장자리는 잘립니다.' : '기기 화면에 맞게 늘려서 표시합니다. 글자와 이미지의 비율이 달라질 수 있습니다.'}</p>
      </PropSection>
      <PropSection title="레이아웃">
        <fieldset disabled={!!pickedDevice} className="min-w-0 border-0 p-0 m-0 disabled:opacity-60">
          <SizeFields w={canvasW} h={canvasH} onChange={onSize} />
        </fieldset>
        <p className="text-[11px] text-text-dim">마지막으로 설정한 크기를 이 브라우저의 새 슬라이드 기본값으로 사용합니다.</p>
      </PropSection>
      <PropSection title="채우기">
        {fill === null ? (
          <button
            type="button"
            onClick={() => onFill('#FFFFFF', fillOpacity || 100)}
            className="self-start text-[13px] px-2.5 py-1.5 border border-line rounded-md hover:border-line-strong"
          >
            + 배경색 추가
          </button>
        ) : (
          <FillControl
            color={fill}
            opacity={fillOpacity}
            onChange={(c, o) => onFill(c, o)}
            onClear={() => onFill(null, fillOpacity)}
          />
        )}
      </PropSection>
      <PropSection title="내보내기">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={onExport}
            disabled={exporting}
            title={exporting ? 'PNG 만드는 중…' : `대지를 PNG로 저장 (${canvasW}×${canvasH}, 숨긴 레이어 제외)`}
            aria-label="대지를 PNG로 저장"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-line bg-panel-2 text-text transition-colors hover:border-line-strong hover:bg-panel disabled:opacity-40"
          >
            <DownloadIcon />
          </button>
          <span className="text-[12px] text-text-dim">PNG · {canvasW}×{canvasH}</span>
        </div>
      </PropSection>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Layer properties panel
// -----------------------------------------------------------------------------
function LayerProperties({
  layer,
  readOnly,
  aspectLocked,
  onAspectLockedChange,
  onEditDataWidget,
  onPatch,
}: {
  layer: NoticeLayer;
  readOnly: boolean;
  aspectLocked: boolean;
  onAspectLockedChange: (locked: boolean) => void;
  onEditDataWidget: () => void;
  onPatch: (patch: Partial<NoticeLayer>) => void;
}) {
  const analog = layer.kind === 'dataWidget' && layer.source.kind === 'clock' && layer.source.mode === 'analog';
  const fitFrame = layer.kind === 'dataWidget' ? fitDataWidgetFrame(layer) : null;
  return (
    <fieldset disabled={readOnly || !!layer.locked} className="flex min-w-0 flex-col gap-4">
      <InspectorTitle>{layer.kind === 'dataWidget' ? `${DATA_WIDGET_LABELS[layer.source.kind]} 위젯` : layer.kind === 'shape' ? `도형 · ${NOTICE_SHAPE_LABELS[layer.shape]}` : layerKindTitle(layer.kind)}</InspectorTitle>
      {layer.kind === 'dataWidget' && <PropSection title="정보 소스">
        <p className="text-[13px] text-text-dim">{layer.title && layer.title !== DATA_WIDGET_LABELS[layer.source.kind] ? layer.title : DATA_WIDGET_LABELS[layer.source.kind]}</p>
        {(layer.source.kind === 'policyNews' || layer.source.kind === 'naverNews') && <NewsSourceSettings source={layer.source} onChange={source => onPatch({ source })} />}
        <Button type="button" aria-label="정보 위젯 설정" className="mt-2" onClick={onEditDataWidget}>상세 설정</Button>
      </PropSection>}

      {layer.kind === 'dataWidget' && <PropSection title="위젯 표시">
        {isLocalTimeSource(layer.source) && <LocalTimeSettings source={layer.source} onChange={source => onPatch({ source })} />}
        {layer.source.kind === 'weather' && <WeatherDisplaySettings value={layer} onChange={patch => {
          if (patch.weatherLayout || patch.weatherFields) {
            const next = { ...layer, ...patch };
            onPatch({ ...patch, ...fitWeatherWidgetSize(next), fontSize: layer.fontSize });
          } else onPatch(patch);
        }} />}
        {layer.source.kind === 'weather' && <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={layer.hideWhenUnavailable ?? false} onChange={event => onPatch({ hideWhenUnavailable: event.target.checked })} />연결 실패·데이터 없음 시 숨김</label>}
        {!analog && <NumField label="글자 크기" value={layer.fontSize} onChange={fontSize => onPatch({ fontSize: Math.max(8, Math.min(1000, fontSize)) })} />}
        <WidgetColorField label={analog ? '바늘 색' : '위젯 글자 색'} value={layer.color} onChange={color => onPatch({ color })} />
        {/* 색을 고르면 그 자체로 배경이 켜진다 — 투명 해제를 먼저 찾아야 하는 순서를 없앤다(2026-09-17 대교). */}
        <WidgetColorField label="위젯 배경 색" value={layer.backgroundColor ?? '#FFFFFF'} onChange={backgroundColor => onPatch({ backgroundColor })} />
        <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={layer.backgroundColor === null} onChange={event => onPatch({ backgroundColor: event.target.checked ? null : '#FFFFFF' })} />배경 투명</label>
        {!analog && <><label className="block space-y-1.5 text-[13px]">글꼴<select className="w-full rounded-md border border-line bg-panel-2 px-2 py-2" aria-label="위젯 글꼴" value={layer.fontFamily ?? 'sans-serif'} onChange={event => onPatch({ fontFamily: event.target.value })}>
          <option value="sans-serif">기기 기본</option><option value="monospace">고정폭</option><option value="serif">명조</option>
          <option value="Pretendard">Pretendard</option><option value="M PLUS 1p">M PLUS 1p (숫자)</option><option value="Moulpali">Moulpali (초 테이프)</option>
        </select></label>
        {layer.source.kind !== 'weather' && <label className="block space-y-1.5 text-[13px]">정렬<select className="w-full rounded-md border border-line bg-panel-2 px-2 py-2" aria-label="위젯 정렬" value={layer.align ?? 'left'} onChange={event => onPatch({ align: event.target.value as 'left' | 'center' | 'right' })}>
          <option value="left">왼쪽</option><option value="center">가운데</option><option value="right">오른쪽</option>
        </select></label>}</>}
      </PropSection>}
      <PropSection title="위치">
        <div className="grid grid-cols-2 gap-2">
          <NumField label="X" value={layer.x} onChange={(v) => onPatch({ x: v })} />
          <NumField label="Y" value={layer.y} onChange={(v) => onPatch({ y: v })} />
        </div>
      </PropSection>

      <PropSection title="레이아웃">
        <SizeFields
          locked={aspectLocked}
          onLockedChange={onAspectLockedChange}
          w={layer.width}
          h={layer.height}
          onChange={(w, h) => onPatch({ width: w, height: h })}
        />
        {fitFrame && <Button type="button" className="mt-2" title="글자 크기는 그대로 두고 상자를 내용 + 기본 여백으로 줄입니다"
          disabled={(['x', 'y', 'width', 'height'] as const).every(key => Math.abs(fitFrame[key] - layer[key]) < 0.5)}
          onClick={() => onPatch({ ...fitFrame, fontSize: (layer as NoticeDataWidgetLayer).fontSize })}>내용에 맞춤</Button>}
      </PropSection>

      {layer.kind === 'text' && (
        <>
          <PropSection title="채우기">
            <FillControl
              color={layer.color}
              opacity={Math.round(layer.opacity * 100)}
              onChange={(c, o) =>
                onPatch({ color: c, opacity: Math.max(0, Math.min(1, o / 100)) })
              }
            />
          </PropSection>
          <PropSection title="글씨체">
            <TextTypeControls layer={layer} onPatch={onPatch} />
          </PropSection>
          <PropSection title="내용">
            <textarea
              value={layer.text}
              onChange={(e) => onPatch({ text: e.target.value })}
              rows={3}
              className="w-full px-2.5 py-2 bg-panel-2 border border-line rounded-md text-[14px] resize-y outline-none focus:border-accent"
            />
          </PropSection>
        </>
      )}

      {layer.kind === 'shape' && <ShapeProperties layer={layer} onPatch={onPatch} />}

      {(layer.kind === 'image' || layer.kind === 'video') && (
        <ImageVideoAdvanced layer={layer} onPatch={onPatch} />
      )}

      {layer.kind === 'lottie' && (
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={layer.loop}
              onChange={(e) => onPatch({ loop: e.target.checked } as Partial<NoticeLayer>)}
              className="accent-accent"
            />
            <span>Loop</span>
          </label>
          <label className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={layer.autoplay}
              onChange={(e) => onPatch({ autoplay: e.target.checked } as Partial<NoticeLayer>)}
              className="accent-accent"
            />
            <span>Autoplay</span>
          </label>
        </div>
      )}

      {layer.kind === 'brickBrief' && (
        <BrickBriefSlotsEditor
          layer={layer}
          onSlotsPatch={(slotsPatch) =>
            onPatch({ slots: { ...layer.slots, ...slotsPatch } } as Partial<NoticeLayer>)
          }
          onSourceChange={(source) =>
            onPatch({ source, variant: 'data' } as Partial<NoticeLayer>)
          }
        />
      )}

      {layer.kind === 'roomStatus' && (
        <RoomStatusEditor
          layer={layer}
          onPatch={(patch) => onPatch(patch as Partial<NoticeLayer>)}
        />
      )}

      {layer.kind === 'roomCard' && (
        <RoomCardEditor
          layer={layer}
          onPatch={(patch) => onPatch(patch as Partial<NoticeLayer>)}
        />
      )}

      {layer.kind === 'roomTemplate' && (
        <RoomTemplateEditor
          layer={layer}
          onPatch={(patch) => onPatch(patch as Partial<NoticeLayer>)}
        />
      )}


    </fieldset>
  );
}

// 이미지/비디오 "추가 옵션" — 목업엔 없지만 맞춤/크롭/반복/음소거 보존(기본 접힘).
function ImageVideoAdvanced({
  layer,
  onPatch,
}: {
  layer: NoticeImageLayer | NoticeVideoLayer;
  onPatch: (patch: Partial<NoticeLayer>) => void;
}) {
  return (
    <details className="group border-t border-line pt-2">
      <summary className="list-none cursor-pointer select-none flex items-center gap-1 text-[12px] font-semibold text-text-dim">
        <span className="transition-transform group-open:rotate-90">›</span>
        추가 옵션
      </summary>
      <div className="flex flex-col gap-3 pt-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-[12px] text-text-dim">맞춤</span>
          <select
            value={layer.fit}
            onChange={(e) =>
              onPatch({ fit: e.target.value as 'cover' | 'contain' | 'stretch' })
            }
            className="px-2.5 py-2 bg-panel-2 border border-line rounded-md text-[14px]"
          >
            <option value="cover">채움 (cover)</option>
            <option value="contain">맞춤 (contain)</option>
            <option value="stretch">늘이기 (stretch)</option>
          </select>
        </label>
        <div className="grid grid-cols-3 gap-2">
          <NumField
            label="X%"
            value={layer.mediaOffsetX ?? 0}
            onChange={(v) => onPatch({ mediaOffsetX: v })}
          />
          <NumField
            label="Y%"
            value={layer.mediaOffsetY ?? 0}
            onChange={(v) => onPatch({ mediaOffsetY: v })}
          />
          <NumField
            label="배율"
            value={layer.mediaScale ?? 1}
            step={0.05}
            min={0}
            onChange={(v) => onPatch({ mediaScale: Math.max(0, v) })}
          />
        </div>
        <button
          type="button"
          onClick={() => onPatch({ mediaScale: 1, mediaOffsetX: 0, mediaOffsetY: 0 })}
          className="self-start text-[12px] text-text-dim hover:text-accent"
        >
          크롭 초기화
        </button>
        {/* 모서리 둥글기 — Station C 카드 이미지(위 140px)처럼 네 모서리를 따로 줄 수 있다. 값은 대지 픽셀. */}
        <CornerRadiusField value={layer.cornerRadius} onChange={(cornerRadius) => onPatch({ cornerRadius } as Partial<NoticeLayer>)} />
        <label className="flex items-center gap-1.5 text-[12px] text-text-dim">
          불투명도
          <input
            type="number"
            min={0}
            max={100}
            value={Math.round(layer.opacity * 100)}
            onChange={(e) =>
              onPatch({
                opacity: Math.max(0, Math.min(1, (Number(e.target.value) || 0) / 100)),
              })
            }
            className="w-16 px-1.5 py-1 bg-panel-2 border border-line rounded-md text-[13px] tabular-nums text-right"
          />
          %
        </label>
        {layer.kind === 'video' && (
          <div className="flex flex-col gap-1.5">
            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={layer.loop}
                onChange={(e) => onPatch({ loop: e.target.checked } as Partial<NoticeLayer>)}
                className="accent-accent"
              />
              반복 재생
            </label>
            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={layer.muted}
                onChange={(e) => onPatch({ muted: e.target.checked } as Partial<NoticeLayer>)}
                className="accent-accent"
              />
              음소거
            </label>
          </div>
        )}
      </div>
    </details>
  );
}

// -----------------------------------------------------------------------------
// Slide rasterizer — uses a fresh 2D canvas to composite each layer. The save
// thumbnail uses the default 640×360 box; 대지 내보내기 passes the canvas size.
// Videos contribute their poster/current frame; missing media draws a placeholder.
// -----------------------------------------------------------------------------
async function renderThumbnail(
  layers: NoticeLayer[],
  mediaById: Map<string, MediaItem>,
  canvasW: number = CANVAS_W,
  canvasH: number = CANVAS_H,
  canvasFill: string | null = null,
  canvasFillOpacity = 100,
  weatherPreviews: WeatherPreviewCache | null = null,
  box: readonly [number, number] = [640, 360],
): Promise<Blob | null> {
  // Fit inside `box` while preserving the scene aspect (so portrait scenes,
  // NBL_REQ_409, render as portrait thumbnails).
  const fit = Math.min(box[0] / canvasW, box[1] / canvasH);
  const OUT_W = Math.max(1, Math.round(canvasW * fit));
  const OUT_H = Math.max(1, Math.round(canvasH * fit));
  const canvas = document.createElement('canvas');
  canvas.width = OUT_W;
  canvas.height = OUT_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, OUT_W, OUT_H);
  if (canvasFill) {
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, canvasFillOpacity / 100));
    ctx.fillStyle = canvasFill;
    ctx.fillRect(0, 0, OUT_W, OUT_H);
    ctx.restore();
  }
  const sx = OUT_W / canvasW;
  const sy = OUT_H / canvasH;

  // Load images/videos in advance. Hidden layers never reach the Player
  // (noticeCompositor skips them), so they stay out of the image too.
  const loaded = await Promise.all(
    layers.filter((layer) => !layer.hidden).map(async (layer) => {
      if (layer.kind === 'image') {
        const media = mediaById.get(layer.mediaId);
        if (!media) return null;
        try {
          const img = await loadImage(api.mediaFileUrl(media.id));
          return { layer, img } as const;
        } catch {
          return null;
        }
      }
      if (layer.kind === 'video') {
        const media = mediaById.get(layer.mediaId);
        if (!media) return null;
        // 서버가 업로드 시 ffmpeg로 만든 poster 썸네일(jpg)을 우선 사용한다.
        // 라이브 <video> 프레임 디코드는 cross-origin Range 요청에서 자주 실패해
        // (편집기 재생은 되지만 canvas drawImage는 검게 누락), 검증된 정적
        // 이미지 경로(loadImage)로 대체한다. 썸네일이 없을 때만 프레임 디코드 시도.
        if (media.thumbnailRelPath) {
          try {
            const img = await loadImage(api.mediaThumbnailUrl(media.id));
            return { layer, img } as const;
          } catch {
            /* poster 로드 실패 — 아래 라이브 프레임으로 폴백 */
          }
        }
        try {
          const video = await loadVideoFrame(api.mediaFileUrl(media.id));
          return { layer, video } as const;
        } catch {
          return null;
        }
      }
      return { layer } as const;
    }),
  );

  for (const entry of loaded
    .filter((e): e is NonNullable<typeof e> => e !== null)
    .sort((a, b) => a.layer.z - b.layer.z)) {
    const l = entry.layer;
    ctx.save();
    const region = noticeGridClip(l, canvasW, canvasH);
    if (region) { ctx.beginPath(); ctx.rect(region.x * sx, region.y * sy, region.width * sx, region.height * sy); ctx.clip(); }
    ctx.globalAlpha = l.opacity;
    const dx = l.x * sx;
    const dy = l.y * sy;
    const dw = l.width * sx;
    const dh = l.height * sy;
    if (l.kind === 'dataWidget' && isLocalTimeSource(l.source)) {
      ctx.scale(sx, sy);
      if (l.rotation) { const cx = l.x + l.width / 2, cy = l.y + l.height / 2; ctx.translate(cx, cy); ctx.rotate(l.rotation * Math.PI / 180); ctx.translate(-cx, -cy); }
      drawTimeWidget(ctx, l);
    } else if (l.kind === 'dataWidget' && l.source.kind === 'weather') {
      ctx.scale(sx, sy);
      if (l.rotation) { const cx = l.x + l.width / 2, cy = l.y + l.height / 2; ctx.translate(cx, cy); ctx.rotate(l.rotation * Math.PI / 180); ctx.translate(-cx, -cy); }
      drawWeatherWidget(ctx, l, cachedWeatherPreview(weatherPreviews, l), canvasFill ?? '#000');
    } else if (l.kind === 'dataWidget' && isNewsListSource(l.source)) {
      ctx.scale(sx, sy);
      if (l.rotation) { const cx = l.x + l.width / 2, cy = l.y + l.height / 2; ctx.translate(cx, cy); ctx.rotate(l.rotation * Math.PI / 180); ctx.translate(-cx, -cy); }
      drawPolicyNewsWidget(ctx, l, cachedWeatherPreview(weatherPreviews, l) ?? newsListSample(l));
    } else if (l.kind === 'shape') {
      ctx.scale(sx, sy);
      drawNoticeShape(ctx, l);
    } else if (l.kind === 'text' || l.kind === 'dataWidget') {
      const text = l.kind === 'dataWidget' ? dataWidgetPlaceholder(l) : l;
      if (text.backgroundColor) {
        ctx.fillStyle = text.backgroundColor;
        const radii = l.kind === 'text' ? cornerRadii(l.cornerRadius, dw, dh, sy) : [0, 0, 0, 0] as const;
        if (radii.some((value) => value > 0)) { traceRoundedRect(ctx, dx, dy, dw, dh, radii); ctx.fill(); }
        else ctx.fillRect(dx, dy, dw, dh);
      }
      ctx.fillStyle = text.color;
      const family = l.kind === 'text' && l.fontFamily ? (/^(sans-serif|serif|monospace)$/.test(l.fontFamily) ? `${l.fontFamily}, ` : `'${l.fontFamily}', `) : '';
      ctx.font = `${text.fontWeight} ${text.fontSize * sy}px ${family}'Inter Tight', 'Pretendard', sans-serif`;
      if ('letterSpacing' in ctx) (ctx as { letterSpacing: string }).letterSpacing = `${l.kind === 'text' ? (l.letterSpacing ?? 0) * sy : 0}px`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = text.align;
      const tx =
        text.align === 'center' ? dx + dw / 2 : text.align === 'right' ? dx + dw : dx + text.paddingX * sx;
      const ty = dy + dh / 2;
      wrapAndFill(ctx, text.text, tx, ty, dw - 2 * text.paddingX * sx, text.fontSize * sy * (l.kind === 'text' ? textLineHeight(l) : 1.2), text.align);
    } else if (l.kind === 'image' && 'img' in entry) {
      drawWithFit(ctx, entry.img, dx, dy, dw, dh, l.fit, l.mediaScale, l.mediaOffsetX, l.mediaOffsetY, cornerRadii(l.cornerRadius, dw, dh, sy));
    } else if (l.kind === 'video') {
      // poster 이미지(우선) 또는 라이브 프레임 — 둘 다 CanvasImageSource.
      const src = 'img' in entry ? entry.img : 'video' in entry ? entry.video : null;
      if (src) {
        drawWithFit(ctx, src, dx, dy, dw, dh, l.fit, l.mediaScale, l.mediaOffsetX, l.mediaOffsetY, cornerRadii(l.cornerRadius, dw, dh, sy));
      }
    } else if (l.kind === 'lottie') {
      drawLottieThumbnail(ctx, dx, dy, dw, dh);
    } else if (l.kind === 'brickBrief') {
      drawBrickBriefThumbnail(ctx, l, dx, dy, dw, dh);
    } else if (l.kind === 'roomStatus') {
      drawRoomStatusThumbnail(ctx, dx, dy, dw, dh);
    } else if (l.kind === 'roomCard') {
      drawRoomCardThumbnail(ctx, l, dx, dy, dw, dh);
    } else if (l.kind === 'roomTemplate') {
      drawRoomTemplateThumbnail(ctx, l, dx, dy, dw, dh);
    }
    ctx.restore();
  }

  return await new Promise<Blob | null>((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), 'image/png');
    } catch {
      // Tainted canvas (CORS) — just skip the thumbnail.
      resolve(null);
    }
  });
}

// Simplified BRICK BRIEF card-only rasterizer for thumbnail generation.
function drawBrickBriefThumbnail(
  ctx: CanvasRenderingContext2D,
  layer: NoticeBrickBriefLayer,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
): void {
  drawBrickBriefDataThumbnail(ctx, layer, dx, dy, dw, dh);
}

// Simplified BRICK BRIEF card-only rasterizer — mirrors the Player runtime
// layout (brickBriefCompositor.ts paintData) without page background/art.
function drawBrickBriefDataThumbnail(
  ctx: CanvasRenderingContext2D,
  layer: NoticeBrickBriefLayer,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(dx, dy, dw, dh);
  ctx.clip();
  const sx = dw / CANVAS_W;
  const sy = dh / CANVAS_H;
  const X = (v: number) => dx + v * sx;
  const Y = (v: number) => dy + v * sy;
  // Card — hard offset shadow, white body, 2px border.
  ctx.fillStyle = '#1350a3';
  ctx.fillRect(X(75), Y(206), 827 * sx, 814 * sy);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(X(68), Y(199), 827 * sx, 814 * sy);
  ctx.strokeStyle = '#1350a3';
  ctx.lineWidth = 2;
  ctx.strokeRect(X(68), Y(199), 827 * sx, 814 * sy);
  // Lime header band.
  ctx.fillStyle = '#dbf790';
  ctx.fillRect(X(68), Y(199), 827 * sx, 101 * sy);
  // Header title.
  ctx.fillStyle = '#00182f';
  ctx.font = `700 ${44 * sy}px 'SUIT', sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(layer.slots.header || '', X(121), Y(199 + 50.5));
  // Label chips + value rows.
  const rows: Array<{
    label: string;
    value: string;
    optional: boolean;
  }> = [
    { label: '제목', value: layer.slots.title, optional: false },
    { label: '일시', value: layer.slots.date, optional: true },
    { label: '시간', value: layer.slots.time, optional: true },
    { label: '장소', value: layer.slots.location, optional: true },
  ];
  let visibleRows = 0;
  for (const row of rows) {
    if (row.optional && row.value.trim() === '') continue;
    const chipY = 341 + visibleRows * 124;
    const valueY = chipY + 45;
    visibleRows += 1;
    ctx.fillStyle = '#dbf790';
    ctx.fillRect(X(124), Y(chipY), 59 * sx, 35 * sy);
    ctx.strokeStyle = '#1350a3';
    ctx.lineWidth = 1;
    ctx.strokeRect(X(124), Y(chipY), 59 * sx, 35 * sy);
    ctx.fillStyle = '#00182f';
    ctx.font = `500 ${20 * sy}px 'SUIT', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(row.label, X(124 + 29.5), Y(chipY + 18));
    ctx.font = `500 ${32 * sy}px 'SUIT', sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(row.value || '—', X(124), Y(valueY));
  }
  // Body paragraph.
  ctx.fillStyle = '#a342f3';
  ctx.font = `600 ${24 * sy}px 'SUIT', sans-serif`;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  layer.slots.content
    .split('\n')
    .slice(0, 3)
    .forEach((line, i) => {
      ctx.fillText(line, X(124), Y(341 + visibleRows * 124 + 1 + i * 30));
    });
  ctx.restore();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Lightweight thumbnail placeholder for room-status layers — the live
// composition pulls real data on the Player, so the persisted PNG is just
// a visual label so admins can recognize the notice in the grid.
function drawRoomStatusThumbnail(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  ctx.fillStyle = '#181b20';
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = '#272a31';
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
  // Pseudo-grid hint
  ctx.strokeStyle = 'rgba(39,42,49,0.8)';
  for (let i = 1; i < 6; i++) {
    const gx = x + (w / 6) * i;
    ctx.beginPath();
    ctx.moveTo(gx, y);
    ctx.lineTo(gx, y + h);
    ctx.stroke();
  }
  // NOW marker
  ctx.strokeStyle = '#f87171';
  ctx.beginPath();
  ctx.moveTo(x + w * 0.45, y);
  ctx.lineTo(x + w * 0.45, y + h);
  ctx.stroke();
  // Label
  ctx.fillStyle = '#ecedef';
  ctx.font = `500 ${Math.max(10, h * 0.18)}px 'Inter Tight', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('회의실 상태 (라이브)', x + w / 2, y + h / 2);
}

function drawRoomCardThumbnail(
  ctx: CanvasRenderingContext2D,
  layer: NoticeRoomCardLayer,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const tokens = ROOM_CARD_THEME_TOKENS[layer.theme];
  const sx = w / CANVAS_W;
  const sy = h / CANVAS_H;
  const X = (v: number) => x + v * sx;
  const Y = (v: number) => y + v * sy;

  ctx.fillStyle = tokens.bg;
  ctx.fillRect(x, y, w, h);

  ctx.fillStyle = tokens.headerText;
  ctx.font = `700 ${56 * sy}px 'Pretendard', sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(`${layer.theme} card`, X(80), Y(104));

  ctx.strokeStyle = tokens.headerText;
  ctx.globalAlpha = 0.25;
  ctx.lineWidth = Math.max(1, 3 * sx);
  ctx.strokeRect(X(1120), Y(184), 720 * sx, 600 * sy);
  ctx.globalAlpha = 1;

  for (let i = 0; i < 3; i++) {
    const cy = Y(184 + i * (148 + 20));
    ctx.fillStyle = tokens.cardBg;
    ctx.fillRect(X(80), cy, 980 * sx, 148 * sy);
    ctx.fillStyle = tokens.timeBlock;
    ctx.fillRect(X(80), cy, 240 * sx, 148 * sy);
    ctx.fillStyle = tokens.timeBlockText;
    ctx.font = `700 ${32 * sy}px 'Pretendard', sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(`0${9 + i}:00`, X(200), cy + 74 * sy);
    ctx.fillStyle = tokens.cardText;
    ctx.font = `500 ${30 * sy}px 'Pretendard', sans-serif`;
    ctx.textAlign = 'left';
    ctx.fillText('회의 일정', X(344), cy + 74 * sy);
  }
}

function drawLottieThumbnail(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  ctx.save();
  ctx.fillStyle = 'rgba(163,66,243,0.18)';
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = '#a342f3';
  ctx.setLineDash([6, 4]);
  ctx.strokeRect(x + 1, y + 1, w - 2, h - 2);
  ctx.setLineDash([]);
  ctx.fillStyle = '#d8b4fe';
  ctx.font = `700 ${Math.max(10, h * 0.18)}px 'Inter Tight', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('LOTTIE', x + w / 2, y + h / 2);
  ctx.restore();
}

function drawRoomTemplateThumbnail(
  ctx: CanvasRenderingContext2D,
  layer: NoticeRoomTemplateLayer,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const layout = ROOM_TEMPLATE_CARD_LAYOUTS[layer.theme];
  const sx = w / CANVAS_W;
  const sy = h / CANVAS_H;
  const X = (v: number) => x + v * sx;
  const Y = (v: number) => y + v * sy;
  for (let i = 0; i < Math.min(3, layout.maxCards); i += 1) {
    const cy = Y(layout.y + i * (layout.height + layout.gap));
    const ch = layout.height * sy;
    ctx.fillStyle = layout.cardBg;
    ctx.fillRect(X(layout.x), cy, layout.width * sx, ch);
    ctx.fillStyle = layout.timeBg;
    ctx.fillRect(X(layout.x), cy, layout.timeWidth * sx, ch);
    ctx.strokeStyle = layout.borderColor;
    ctx.lineWidth = Math.max(1, layout.borderWidth * sy);
    ctx.strokeRect(X(layout.x), cy, layout.width * sx, ch);
    ctx.fillStyle = layout.timeText;
    ctx.font = `700 ${Math.max(8, layout.timeFontSize * sy)}px 'Pretendard', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('14:00', X(layout.x + layout.timeWidth / 2), cy + ch / 2);
    ctx.fillStyle = layout.titleText;
    ctx.font = `700 ${Math.max(8, layout.titleFontSize * sy)}px 'Pretendard', sans-serif`;
    ctx.textAlign = 'left';
    ctx.fillText('회의 일정', X(layout.x + layout.timeWidth + layout.titleOffsetX), cy + ch / 2);
  }
}

function loadVideoFrame(src: string): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.src = src;
    video.playsInline = true;
    // preload='metadata'는 브라우저에 따라 첫 프레임을 디코드하지 않아
    // drawImage()가 아무것도 그리지 못한다(영상 레이어만 썸네일에서 누락).
    // metadata 로드 후 살짝 seek해 프레임 디코드를 강제하고, 프레임이 실제로
    // 준비된 시점(seeked / requestVideoFrameCallback)에 resolve한다.
    video.preload = 'auto';

    const cleanup = (): void => {
      clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onseeked = null;
      video.onerror = null;
    };
    // 이벤트가 오지 않으면 renderThumbnail 전체가 멈추므로 상한을 둔다.
    // 실패는 호출부 catch가 해당 레이어만 스킵하고 나머지는 정상 렌더.
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('video frame load timeout'));
    }, 5000);

    video.onerror = () => {
      cleanup();
      reject(new Error('video load error'));
    };
    video.onloadedmetadata = () => {
      try {
        video.currentTime = Number.isFinite(video.duration)
          ? Math.min(0.1, video.duration / 2)
          : 0;
      } catch {
        cleanup();
        reject(new Error('video seek failed'));
      }
    };
    video.onseeked = () => {
      const done = (): void => {
        cleanup();
        resolve(video);
      };
      // 지원 브라우저에서는 프레임이 합성 준비된 시점까지 정확히 기다린다.
      const rvfc = (
        video as HTMLVideoElement & {
          requestVideoFrameCallback?: (cb: () => void) => number;
        }
      ).requestVideoFrameCallback;
      if (typeof rvfc === 'function') rvfc.call(video, done);
      else done();
    };
    // Kick preload
    video.load();
  });
}

function drawWithFit(
  ctx: CanvasRenderingContext2D,
  src: CanvasImageSource,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
  fit: 'cover' | 'contain' | 'stretch',
  mediaScale = 1,
  mediaOffsetX = 0,
  mediaOffsetY = 0,
  radii: readonly [number, number, number, number] = [0, 0, 0, 0],
): void {
  const clip = () => {
    if (radii.some((value) => value > 0)) traceRoundedRect(ctx, dx, dy, dw, dh, radii);
    else { ctx.beginPath(); ctx.rect(dx, dy, dw, dh); }
    ctx.clip();
  };
  const sw =
    src instanceof HTMLVideoElement
      ? src.videoWidth
      : src instanceof HTMLImageElement
        ? src.naturalWidth
        : 0;
  const sh =
    src instanceof HTMLVideoElement
      ? src.videoHeight
      : src instanceof HTMLImageElement
        ? src.naturalHeight
        : 0;
  if (!sw || !sh) return;
  const scale = Math.max(0, mediaScale);
  if (scale === 0) return;
  if (fit === 'stretch') {
    const drawW = dw * scale;
    const drawH = dh * scale;
    const cx = dx + dw / 2 + (mediaOffsetX / 100) * dw;
    const cy = dy + dh / 2 + (mediaOffsetY / 100) * dh;
    ctx.save();
    clip();
    ctx.drawImage(src, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
    ctx.restore();
    return;
  }
  const srcRatio = sw / sh;
  const dstRatio = dw / dh;
  let drawW = dw;
  let drawH = dh;
  let drawX = dx;
  let drawY = dy;
  if (fit === 'cover') {
    if (srcRatio > dstRatio) {
      // source is wider — match height, crop sides
      drawW = dh * srcRatio;
      drawX = dx - (drawW - dw) / 2;
    } else {
      drawH = dw / srcRatio;
      drawY = dy - (drawH - dh) / 2;
    }
  } else {
    if (srcRatio > dstRatio) {
      drawH = dw / srcRatio;
      drawY = dy + (dh - drawH) / 2;
    } else {
      drawW = dh * srcRatio;
      drawX = dx + (dw - drawW) / 2;
    }
  }
  drawW *= scale;
  drawH *= scale;
  const cx = dx + dw / 2 + (mediaOffsetX / 100) * dw;
  const cy = dy + dh / 2 + (mediaOffsetY / 100) * dh;
  drawX = cx - drawW / 2;
  drawY = cy - drawH / 2;
  ctx.save();
  clip();
  ctx.drawImage(src, drawX, drawY, drawW, drawH);
  ctx.restore();
}

function wrapAndFill(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  align: 'left' | 'center' | 'right',
): void {
  if (!text) return;
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  const totalHeight = (lines.length - 1) * lineHeight;
  const startY = y - totalHeight / 2;
  lines.forEach((line, i) => {
    const lx =
      align === 'center' ? x : align === 'right' ? x : x;
    ctx.fillText(line, lx, startY + i * lineHeight);
  });
  void align;
}

// =============================================================================
// 저장 팝업 — 슬라이드명 · 재생시간 (Figma). 확인 시 실제 저장 수행.
// 폴더 필드는 2026-09-16 대교 결정으로 없앴다 — 콘텐츠는 소유 그룹으로만 나뉜다.
// =============================================================================
/** 이름을 비워 둔 새 슬라이드의 기본 이름 — 저장 모달의 안내문과 저장 값이 같은 출처를 쓴다. */
const NEW_SLIDE_NAME = '새 슬라이드';

function SlideSaveModal({
  initialName,
  initialDurationSec,
  saving,
  onCancel,
  onConfirm,
}: {
  initialName: string;
  initialDurationSec: number;
  saving: boolean;
  onCancel: () => void;
  onConfirm: (meta: { name: string; durationSec: number }) => void;
}) {
  const [name, setName] = useState(initialName?.trim() ?? '');
  const [dur, setDur] = useState(initialDurationSec > 0 ? String(initialDurationSec) : '');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    // 비워 두면 안내에 적힌 이름으로 저장한다. 예전에는 '새 공지'가 값으로 들어 있어서,
    // 안내문처럼 보이지만 지우지 않으면 그대로 진짜 이름이 됐다(2026-09-17 대교).
    const nm = name.trim() || initialName.trim() || NEW_SLIDE_NAME;
    const n = Number(dur);
    if (!Number.isFinite(n) || n <= 0) {
      setError('슬라이드 재생시간을 숫자(초)로 입력하세요.');
      return;
    }
    onConfirm({ name: nm, durationSec: Math.round(n) });
  };

  const labelCls = 'text-[13px] font-medium flex items-center gap-0.5';
  const inputCls =
    'w-full px-2.5 py-2 bg-panel-2 border border-line rounded-md text-[14px] outline-none focus:border-accent';
  return (
    <ModalShell title="슬라이드 저장" onClose={onCancel}>
      <label className="flex flex-col gap-1.5">
        <span className={labelCls}>슬라이드명</span>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          placeholder={initialName.trim() || NEW_SLIDE_NAME}
          className={inputCls}
        />
        <span className="text-[12px] text-text-dim">비워 두면 ‘{initialName.trim() || NEW_SLIDE_NAME}’(으)로 저장합니다.</span>
      </label>
      <label className="flex flex-col gap-1.5">
        <span className={labelCls}>
          슬라이드 재생시간 <span className="text-red">*</span>
        </span>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            step={1}
            value={dur}
            onChange={(e) => setDur(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            placeholder="숫자만 입력"
            className={cn(inputCls, 'flex-1 tabular-nums')}
          />
          <span className="text-[13px] text-text-dim">초</span>
        </div>
      </label>
      {/* 폴더 선택도, 공개/비공개도 없다 — 콘텐츠는 소유 그룹으로만 나뉜다(2026-09-16·17 대교). */}
      {error && <div className="text-[12px] text-red">{error}</div>}
      <footer className="flex items-center justify-end gap-2 pt-1">
        <Button onClick={onCancel}>취소</Button>
        <Button variant="primary" onClick={submit} disabled={saving}>
          {saving ? '저장 중…' : '확인'}
        </Button>
      </footer>
    </ModalShell>
  );
}

// =============================================================================
// 하단 툴바 — 버튼 / 구분자 / 레이어 종류·기능 아이콘 (모두 currentColor SVG).
// =============================================================================
function ToolbarBtn({
  onClick,
  disabled,
  active,
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={cn(
        // 툴바는 항상 다크 스테이지(#101114) 위에 뜨므로 테마 무관 고정 밝은 톤.
        'w-8 h-8 flex items-center justify-center rounded-md text-white/70 transition-colors',
        'hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed',
        active && 'bg-white/15 text-accent hover:text-accent',
      )}
    >
      {children}
    </button>
  );
}

function ClockWidgetIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="8" cy="8" r="6" />
    <path d="M8 4.5V8l2.5 1.5" />
  </svg>;
}

function DateWidgetIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true"><rect x="2" y="3" width="12" height="11" rx="2" /><path d="M5 1v4m6-4v4M2 7h12" /><path d="M5 10h2m2 0h2M5 12h2" /></svg>;
}
function WeekdayWidgetIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true"><rect x="2" y="2" width="12" height="12" rx="3" /><path d="m5 8 2 2 4-5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function WeatherWidgetIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7.5 5.5a3 3 0 1 0-3 4M5 1v1M1 5h1M2 2l.8.8M8 2l-.8.8" />
    <path d="M6 13h6a2.5 2.5 0 0 0 .2-5A3.5 3.5 0 0 0 5.5 9 2 2 0 0 0 6 13Z" />
  </svg>;
}

function DataWidgetIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="4.5" height="4.5" rx="1" />
    <rect x="9.5" y="2" width="4.5" height="4.5" rx="1" />
    <rect x="2" y="9.5" width="4.5" height="4.5" rx="1" />
    <path d="M9.5 11.75H14m-2.25-2.25V14" />
  </svg>;
}

// 라벨은 10px·흰색 50%였는데 너무 작고 흐렸다 → 12px·흰색 70%(어두운 툴바 대비 약 9:1) (2026-09-16 검수 C2).
function ToolbarDivider({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-1.5 self-stretch pl-1">
      <div className="w-px self-stretch bg-white/15" />
      {label && (
        <span className="text-[12px] font-mono uppercase tracking-monoWide text-white/70 select-none">
          {label}
        </span>
      )}
    </div>
  );
}

function LayerKindIcon({ kind, sourceKind }: { kind: NoticeLayer['kind']; sourceKind?: NoticeDataWidgetLayer['source']['kind'] }) {
  const box = 'w-5 h-5 shrink-0 flex items-center justify-center text-text-dim';
  if (kind === 'dataWidget') return <span className={box}>{sourceKind === 'clock' ? <ClockWidgetIcon /> : sourceKind === 'date' ? <DateWidgetIcon /> : sourceKind === 'weekday' ? <WeekdayWidgetIcon /> : sourceKind === 'weather' ? <WeatherWidgetIcon /> : <DataWidgetIcon />}</span>;
  if (kind === 'text')
    return <span className={cn(box, 'font-display text-[13px] font-bold')}>T</span>;
  if (kind === 'image')
    return (
      <span className={box}>
        <ImageIcon />
      </span>
    );
  if (kind === 'video')
    return (
      <span className={box}>
        <VideoIcon />
      </span>
    );
  return <span className={cn(box, 'text-[11px]')}>◆</span>;
}

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M1.5 8S3.9 3.5 8 3.5 14.5 8 14.5 8 12.1 12.5 8 12.5 1.5 8 1.5 8Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3" />
      {off && (
        <path d="M3 13L13 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      )}
    </svg>
  );
}

function LockIcon({ locked }: { locked: boolean }) {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="3.5" y="7" width="9" height="6.5" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
      {locked ? (
        <path d="M5.5 7V5.2a2.5 2.5 0 0 1 5 0V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      ) : (
        <path d="M5.5 7V5.2a2.5 2.5 0 0 1 4.8-1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      )}
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 4v11" /><path d="m7 10 5 5 5-5" /><path d="M5 20h14" />
    </svg>
  );
}

function UndoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M5 5.5H10a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.5 3L4 5.5 6.5 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RedoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M11 5.5H6a3.5 3.5 0 0 0 0 7h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.5 3L12 5.5 9.5 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CursorIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 3L12.5 7 8.7 8.7 7 12.5 4 3Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" fill="currentColor" fillOpacity="0.15" />
    </svg>
  );
}

function MoveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M8 2L6.4 3.6M8 2l1.6 1.6M8 14l-1.6-1.6M8 14l1.6-1.6M2 8l1.6-1.6M2 8l1.6 1.6M14 8l-1.6-1.6M14 8l-1.6 1.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2" y="3" width="12" height="10" rx="1.4" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="5.5" cy="6.5" r="1.1" stroke="currentColor" strokeWidth="1.1" />
      <path d="M3 12l3.5-3.5 2.5 2.5L11 9l2 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2" y="3.5" width="12" height="9" rx="1.6" stroke="currentColor" strokeWidth="1.3" />
      <path d="M7 6.3l3 1.7-3 1.7V6.3Z" fill="currentColor" />
    </svg>
  );
}

function LayoutGridIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2" y="2" width="12" height="12" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <path d="M6 2v12M10 2v12M2 6h12M2 10h12" stroke="currentColor" strokeWidth="1" strokeOpacity="0.7" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M12.5 8a4.5 4.5 0 1 1-1.3-3.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M12.5 2.5V5H10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// =============================================================================
// 우측 인스펙터 공용 요소 — 섹션/필드/색·비율 컨트롤 (대지·텍스트·이미지·비디오 공유).
// =============================================================================

// 사용 가능한 글꼴 — 앱에 로드된 폰트 우선(없으면 시스템으로 폴백).
// 번들 글꼴 — globals.css @font-face 와 Player 가 같은 파일을 싣는다(솔뫼 김대건: Station C 캠페인 템플릿, 2026-09-22).
const FONT_FAMILIES: string[] = ['Pretendard', 'Solmoe KimDaeGeon', 'Inter Tight', 'sans-serif'];

// 두께 — 이름 ↔ 숫자. NoticeFontWeight 범위(100–900).
const WEIGHT_OPTIONS: Array<{ value: NoticeFontWeight; label: string }> = [
  { value: 100, label: 'Thin' },
  { value: 200, label: 'ExtraLight' },
  { value: 300, label: 'Light' },
  { value: 400, label: 'Regular' },
  { value: 500, label: 'Medium' },
  { value: 600, label: 'SemiBold' },
  { value: 700, label: 'Bold' },
  { value: 800, label: 'ExtraBold' },
  { value: 900, label: 'Black' },
];

// 글씨 크기 프리셋(가상 캔버스 px). 현재 값이 목록에 없으면 앞에 끼워 넣는다.

function layerKindTitle(kind: NoticeLayer['kind']): string {
  switch (kind) {
    case 'dataWidget':
      return '정보 위젯';
    case 'text':
      return '텍스트';
    case 'shape':
      return '도형';
    case 'image':
      return '이미지';
    case 'video':
      return '비디오';
    case 'lottie':
      return 'Lottie';
    case 'brickBrief':
      return 'BRICK BRIEF';
    case 'roomStatus':
      return '회의실 보드';
    case 'roomCard':
      return '회의실 카드';
    case 'roomTemplate':
      return '회의실 템플릿';
    default:
      return '레이어';
  }
}

function InspectorTitle({ children }: { children: ReactNode }) {
  return (
    <div className="text-[15px] font-semibold text-text pb-2.5 border-b border-line">
      {children}
    </div>
  );
}

// 도형 속성 — 색을 고르면 그 자체로 채우기·선이 켜지고, '없음'으로 끈다(위젯 배경과 같은 방식).
function ShapeProperties({ layer, onPatch }: { layer: NoticeShapeLayer; onPatch: (patch: Partial<NoticeShapeLayer>) => void }) {
  const line = layer.shape === 'line';
  const selectCls = 'w-full rounded-md border border-line bg-panel-2 px-2 py-2 text-[13px]';
  return (
    <>
      {!line && (
        <PropSection title="채우기">
          <WidgetColorField alpha label="채우기 색" value={layer.fill ?? '#D9D9D9'} onChange={(fill) => onPatch({ fill })} />
          <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={layer.fill === null} onChange={(e) => onPatch({ fill: e.target.checked ? null : '#D9D9D9' })} />채우기 없음</label>
        </PropSection>
      )}
      <PropSection title="선">
        <WidgetColorField alpha label="선 색" value={layer.stroke ?? '#FFFFFF'} onChange={(stroke) => onPatch({ stroke, strokeWidth: layer.strokeWidth || 4 })} />
        {!line && <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={layer.stroke === null} onChange={(e) => onPatch(e.target.checked ? { stroke: null } : { stroke: '#FFFFFF', strokeWidth: layer.strokeWidth || 4 })} />선 없음</label>}
        <NumField label="두께" value={layer.strokeWidth} min={0} onChange={(v) => onPatch({ strokeWidth: Math.max(0, Math.min(200, v)) })} />
      </PropSection>
      {(layer.shape === 'rectangle' || layer.shape === 'speechBubble') && (
        <PropSection title="모서리">
          <NumField label="둥글기" value={layer.cornerRadius ?? 0} min={0} onChange={(v) => onPatch({ cornerRadius: Math.max(0, v) })} />
        </PropSection>
      )}
      {(layer.shape === 'polygon' || layer.shape === 'star') && (
        <PropSection title={layer.shape === 'star' ? '꼭짓점 수' : '변의 수'}>
          <select aria-label={layer.shape === 'star' ? '꼭짓점 수' : '변의 수'} className={selectCls} value={noticeShapeSides(layer)} onChange={(e) => onPatch({ sides: Number(e.target.value) })}>
            {Array.from({ length: 10 }, (_, i) => i + 3).map((count) => <option key={count} value={count}>{count}</option>)}
          </select>
        </PropSection>
      )}
      {line && (
        <PropSection title="방향">
          <Segmented
            value={layer.lineDirection ?? 'horizontal'}
            options={[{ value: 'horizontal', label: '가로' }, { value: 'vertical', label: '세로' }, { value: 'down', label: '↘' }, { value: 'up', label: '↗' }]}
            onChange={(lineDirection) => {
              // 가로↔세로는 상자도 눕히거나 세워 선 길이를 유지한다.
              const swap = (lineDirection === 'vertical' && layer.width > layer.height) || (lineDirection === 'horizontal' && layer.height > layer.width);
              onPatch(swap ? { lineDirection, width: layer.height, height: layer.width } : { lineDirection });
            }}
          />
        </PropSection>
      )}
    </>
  );
}

function ShapesIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" aria-hidden="true">
    <rect x="1.5" y="6.5" width="8" height="8" rx="1" />
    <circle cx="10.5" cy="5.5" r="4" />
  </svg>;
}

/** 메뉴 아이콘도 실제 도형 경로로 그려 모양이 어긋나지 않게 한다. */
function ShapeIcon({ shape }: { shape: NoticeShapeKind }) {
  return <svg width="16" height="16" viewBox="-1.5 -1.5 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true">
    <path d={noticeShapePath({ shape, width: 13, height: 13, stroke: null, strokeWidth: 0, cornerRadius: 2, lineDirection: 'up' })} />
  </svg>;
}

function PropSection({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[12px] font-semibold text-text-dim">{title}</div>
      {children}
    </div>
  );
}

// 라벨 접두형 숫자 필드 — 목업의 "X 9,999" / "W 9,999" 스타일.
function NumField({
  label,
  value,
  onChange,
  min,
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  step?: number;
}) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-2 bg-panel-2 border border-line rounded-md focus-within:border-accent">
      <span className="text-[13px] text-text-dimmer shrink-0">{label}</span>
      <input
        aria-label={label}
        type="number"
        value={String(value)}
        min={min}
        step={step}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-full min-w-0 bg-transparent outline-none text-[14px] text-text tabular-nums"
      />
    </div>
  );
}

// W/H 필드 + 비율 잠금 토글. 잠금 시 한 축을 바꾸면 비율 유지해 다른 축 자동.
function SizeFields({
  w,
  h,
  onChange,
  defaultLocked = false,
  locked,
  onLockedChange,
}: {
  w: number;
  h: number;
  onChange: (w: number, h: number) => void;
  defaultLocked?: boolean;
  locked?: boolean;
  onLockedChange?: (locked: boolean) => void;
}) {
  const [localLock, setLocalLock] = useState(defaultLocked);
  const lock = locked ?? localLock;
  const ratio = w > 0 && h > 0 ? w / h : 1;
  return (
    <div className="flex items-stretch gap-2">
      <div className="grid grid-cols-2 gap-2 flex-1 min-w-0">
        <NumField
          label="W"
          value={w}
          min={1}
          onChange={(nw) => {
            const nextW = Math.max(1, nw);
            onChange(nextW, lock ? Math.max(1, Math.round(nextW / ratio)) : h);
          }}
        />
        <NumField
          label="H"
          value={h}
          min={1}
          onChange={(nh) => {
            const nextH = Math.max(1, nh);
            onChange(lock ? Math.max(1, Math.round(nextH * ratio)) : w, nextH);
          }}
        />
      </div>
      <button
        type="button"
        onClick={() => { const next = !lock; if (onLockedChange) onLockedChange(next); else setLocalLock(next); }}
        title={lock ? '비율 고정 해제' : '비율 고정'}
        aria-label={lock ? '비율 고정 해제' : '비율 고정'}
        aria-pressed={lock}
        className={cn(
          'w-9 shrink-0 flex items-center justify-center rounded-md border transition-colors',
          lock
            ? 'border-accent bg-accent-soft text-accent'
            : 'border-line text-text-dim hover:text-text hover:border-line-strong',
        )}
      >
        <AspectLockIcon locked={lock} />
      </button>
    </div>
  );
}

// 색 스와치 + HEX + 불투명도(%). onClear 있으면 × 노출(대지 채우기 제거용).
function FillControl({
  color,
  opacity,
  onChange,
  onClear,
}: {
  color: string;
  opacity: number;
  onChange: (color: string, opacity: number) => void;
  onClear?: () => void;
}) {
  // color 입력이 유효한 hex일 때만 <input type=color>에 반영(임의 CSS 색 허용).
  const swatch = /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#FFFFFF';
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={swatch}
        onChange={(e) => onChange(e.target.value, opacity)}
        className="w-8 h-8 shrink-0 rounded border border-line bg-transparent cursor-pointer p-0"
      />
      <input
        type="text"
        value={color}
        onChange={(e) => onChange(e.target.value, opacity)}
        className="flex-1 min-w-0 px-2.5 py-2 bg-panel-2 border border-line rounded-md text-[14px] font-mono uppercase outline-none focus:border-accent"
      />
      <div className="flex items-center gap-1 shrink-0">
        <input
          type="number"
          min={0}
          max={100}
          value={opacity}
          onChange={(e) =>
            onChange(color, Math.max(0, Math.min(100, Number(e.target.value) || 0)))
          }
          className="w-14 px-1.5 py-2 bg-panel-2 border border-line rounded-md text-[14px] tabular-nums text-right outline-none focus:border-accent"
        />
        <span className="text-[13px] text-text-dim">%</span>
      </div>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          title="채우기 제거"
          className="w-6 h-6 shrink-0 text-text-dim hover:text-red"
        >
          ×
        </button>
      )}
    </div>
  );
}

// 텍스트 글씨체 — 글꼴 / (두께 · 크기) / (가로정렬 · 세로정렬).
function TextTypeControls({
  layer,
  onPatch,
}: {
  layer: NoticeTextLayer;
  onPatch: (patch: Partial<NoticeLayer>) => void;
}) {
  const [fontSizeInput, setFontSizeInput] = useState(String(layer.fontSize));
  useEffect(() => { setFontSizeInput(String(layer.fontSize)); }, [layer.id, layer.fontSize]);
  const commitFontSize = () => {
    const parsed = Number(fontSizeInput);
    const size = fontSizeInput.trim() && Number.isFinite(parsed) ? Math.max(8, Math.min(1000, parsed)) : layer.fontSize;
    setFontSizeInput(String(size));
    if (size !== layer.fontSize) onPatch({ fontSize: size });
  };
  const va = layer.verticalAlign ?? 'middle';
  const selectCls =
    'px-2.5 py-2 bg-panel-2 border border-line rounded-md text-[14px] outline-none focus:border-accent';
  return (
    <div className="flex flex-col gap-2">
      <select
        value={layer.fontFamily ?? ''}
        onChange={(e) => onPatch({ fontFamily: e.target.value || undefined } as Partial<NoticeLayer>)}
        className={cn(selectCls, 'w-full')}
        style={{ fontFamily: layer.fontFamily || undefined }}
      >
        <option value="">기본 (Pretendard)</option>
        {FONT_FAMILIES.map((f) => (
          <option key={f} value={f} style={{ fontFamily: f }}>
            {f}
          </option>
        ))}
      </select>
      <div className="grid grid-cols-2 gap-2">
        <select
          value={layer.fontWeight}
          onChange={(e) =>
            onPatch({ fontWeight: Number(e.target.value) as NoticeFontWeight } as Partial<NoticeLayer>)
          }
          className={selectCls}
        >
          {WEIGHT_OPTIONS.map((w) => (
            <option key={w.value} value={w.value}>
              {w.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          aria-label="글자 크기"
          title="글자 크기(px), 8–1000"
          min={8}
          max={1000}
          step="any"
          value={fontSizeInput}
          onChange={(e) => setFontSizeInput(e.target.value)}
          onBlur={commitFontSize}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); e.currentTarget.blur(); } }}
          className={selectCls}
        />
      </div>
      <div className="flex items-center gap-2">
        <IconSeg
          value={layer.align}
          onChange={(v) => onPatch({ align: v })}
          options={[
            { value: 'left', title: '왼쪽', icon: <AlignLeftIcon /> },
            { value: 'center', title: '가운데', icon: <AlignCenterIcon /> },
            { value: 'right', title: '오른쪽', icon: <AlignRightIcon /> },
          ]}
        />
        <IconSeg
          value={va}
          onChange={(v) => onPatch({ verticalAlign: v } as Partial<NoticeLayer>)}
          options={[
            { value: 'top', title: '위', icon: <VAlignTopIcon /> },
            { value: 'middle', title: '중간', icon: <VAlignMiddleIcon /> },
            { value: 'bottom', title: '아래', icon: <VAlignBottomIcon /> },
          ]}
        />
      </div>
      {/* 줄간격(배) · 자간(px) — Figma 템플릿의 1.26/1.4 줄간격과 -2.4px 자간을 그대로 담는다. */}
      <div className="grid grid-cols-2 gap-2">
        <NumField label="줄간격" value={layer.lineHeight ?? 1.2} step={0.05} min={0.5}
          onChange={(v) => onPatch({ lineHeight: Math.max(0.5, Math.min(4, v)) } as Partial<NoticeLayer>)} />
        <NumField label="자간" value={layer.letterSpacing ?? 0} step={0.1}
          onChange={(v) => onPatch({ letterSpacing: v || undefined } as Partial<NoticeLayer>)} />
      </div>
      <CornerRadiusField value={layer.cornerRadius} onChange={(cornerRadius) => onPatch({ cornerRadius } as Partial<NoticeLayer>)} />
    </div>
  );
}

/** 모서리 둥글기 — 한 값(전체) 또는 ↖ ↗ ↘ ↙ 네 값. 0이면 저장하지 않는다(undefined). */
function CornerRadiusField({ value, onChange }: { value: NoticeCornerRadius | undefined; onChange: (next: NoticeCornerRadius | undefined) => void }) {
  const radii = Array.isArray(value) ? value : [value ?? 0, value ?? 0, value ?? 0, value ?? 0];
  const [perCorner, setPerCorner] = useState(Array.isArray(value));
  const commit = (next: number[]) => {
    const clean = next.map((n) => Math.max(0, Number.isFinite(n) ? n : 0));
    if (clean.every((n) => n === 0)) return onChange(undefined);
    onChange(clean.every((n) => n === clean[0]) && !perCorner ? clean[0]! : [clean[0]!, clean[1]!, clean[2]!, clean[3]!]);
  };
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-[12px] text-text-dim">
        <span>모서리 둥글기</span>
        <button type="button" className="hover:text-accent" onClick={() => setPerCorner((v) => !v)}>{perCorner ? '한 값으로' : '모서리별'}</button>
      </div>
      {perCorner ? (
        <div className="grid grid-cols-4 gap-1.5">
          {(['↖', '↗', '↘', '↙'] as const).map((label, index) => (
            <NumField key={label} label={label} value={radii[index] ?? 0} min={0}
              onChange={(v) => { const next = [...radii]; next[index] = v; commit(next); }} />
          ))}
        </div>
      ) : (
        <NumField label="px" value={radii[0] ?? 0} min={0} onChange={(v) => commit([v, v, v, v])} />
      )}
    </div>
  );
}

// 아이콘 세그먼트(정렬류) — 제네릭 선택 그룹.
function IconSeg<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; title: string; icon: ReactNode }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-line overflow-hidden">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          title={o.title}
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'w-8 h-8 flex items-center justify-center border-l border-line first:border-l-0',
            value === o.value
              ? 'bg-accent-soft text-accent'
              : 'text-text-dim hover:text-text hover:bg-panel-2',
          )}
        >
          {o.icon}
        </button>
      ))}
    </div>
  );
}

function AspectLockIcon({ locked }: { locked: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {locked ? <>
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </> : <>
        <path d="m18.84 15.12 1.7-1.7a5 5 0 0 0-7.07-7.07l-1.71 1.71M5.16 8.88l-1.7 1.7a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        <path d="M8 2v3M2 8h3M16 19v3M19 16h3" />
      </>}
    </svg>
  );
}

function AlignLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2.5 4h11M2.5 8h7M2.5 12h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function AlignCenterIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2.5 4h11M4.5 8h7M3.5 12h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function AlignRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2.5 4h11M6.5 8h7M4.5 12h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function VAlignTopIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 3h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <rect x="6.5" y="5" width="3" height="8" rx="0.6" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}
function VAlignMiddleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <rect x="6.5" y="3.5" width="3" height="9" rx="0.6" stroke="currentColor" strokeWidth="1.3" fill="none" />
    </svg>
  );
}
function VAlignBottomIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 13h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <rect x="6.5" y="3" width="3" height="8" rx="0.6" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

function LiveWidgetBody({ layer }: { layer: NoticeDataWidgetLayer }) {
  if (layer.source.kind === 'weather') return <WeatherWidgetPreview layer={layer} />;
  if (isNewsListSource(layer.source)) return <PolicyNewsWidgetPreview layer={layer} />;
  if (isLocalTimeSource(layer.source)) return <TimeWidgetCanvas layer={layer} />;
  return <TextLayerBody layer={dataWidgetPlaceholder(layer)} editing={false} onCommit={() => undefined} onEndEdit={() => undefined} />;
}
